/**
 * InfinitSales - Chat Window Component (SSE Optimized + AI Workspace v3)
 * WhatsApp-style message display with real-time SSE updates
 * 
 * AI WORKSPACE WITH FILE ATTACHMENTS:
 * - Supports artifacts/files from AI responses
 * - Files are attached when using "Usar resposta"
 * - Shows file preview in input area
 * - Sends text + files via sendMessageWithMedia endpoint
 * 
 * ✅ FIX: History now uses integrated useAIWorkspace hook
 * ✅ FIX: Now uses sendMessageWithMedia for text + files
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Send,
  Mic,
  MoreVertical,
  Bell,
  User,
  UserCheck,
  Users,
  Loader2,
  Image as ImageIcon,
  FileText,
  ArrowDown,
  Check,
  CheckCheck,
  AlertCircle,
  Bot,
  Clock,
  Zap,  ArrowLeft,
  BanIcon,
  Tags,
  Tag,
  MessageSquarePlus,
  History,
  Settings,
  Forward,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { formatDayLabel } from '../../components/chat/ChatDateDivider';
import { statusInfo } from '../../components/chat/MessageStatus';
import { useSSEEvent, SSE_EVENTS } from '../../contexts/SSEContext';
// Frente C4 (flag chat_cache): conversa ja vista pinta na hora.
// Flag message_actions: responder citando, reagir, editar e encaminhar.
import useFeatureFlags, { getFeatureFlag } from '../../hooks/useFeatureFlags';
import useAgentAnswers from '../../hooks/useAgentAnswers';
import {
  MessageActionMenu,
  ReactionChips,
  QuotedMessage,
  ReplyPreview,
  SelectionBar,
  messageActionCaps,
  whyCannotEdit,
  quotedFromMessage,
} from '../../components/messages';
import { getChatEntry, setChatEntry, setChatMessages } from '../../services/inbox/chatCache';
import { useNotifications } from '../../contexts/NotificationsContext';
import { useAuth } from '../../contexts/AuthContext';
import renderVariablesInText from '../../utils/messageVariables';
import { getDelegation } from '../../utils/delegation';
import CreateNotificationModal from '../../components/notifications/CreateNotificationModal';
import HandoffBanner from '../../components/HandoffBanner';
import whatsappService from '../../services/wa-personal/whatsappService';
import chatUtils from '../../utils/wa-personal/chat_utils';

// AI Workspace imports
import {
  AIWorkspace,
  MessageCheckbox,
  MessageRolePill,
  AttachedFilesPreview,
  useAIWorkspace,
  getWorkspaceColors,
  BLOCKED_MESSAGE_TYPES,
  MAX_SELECTED_MESSAGES,
  ACTION_TYPES,
  extractMessagesPayload,
} from '../../components/AIWorkspace';
import aiAgentService from '../../services/global/aiAgentService';
import fileUploadService from '../../services/global/fileUploadService';
import MessageMedia from '../../components/MessageMedia';
import ScheduleMessageModal from '../../components/followups/ScheduleMessageModal';
import FollowupBadge from '../../components/followups/FollowupBadge';
import LabelPickerModal from '../../components/labels/LabelPickerModal';
import TransferContactModal from '../../components/contacts/TransferContactModal';
import ContactFeedbackModal from '../../components/contacts/ContactFeedbackModal';
import ContactDetailsDrawer from '../../components/contacts/ContactDetailsDrawer';
import ContactChannelChips from '../../components/contacts/ContactChannelChips';
import ErrorBoundary, { DrawerErrorFallback } from '../../components/ErrorBoundary';
import RequiredFieldsSignal from '../../components/contacts/RequiredFieldsSignal';
import RequiredFieldsPrompt from '../../components/contacts/RequiredFieldsPrompt';
import ContactTagsManagerModal from '../../components/contacts/ContactTagsManagerModal';
import SavedMessagesDrawer from '../../components/messages/SavedMessagesDrawer';
import ForwardModal from '../../components/messages/ForwardModal';
import WorkspaceConfigDrawer from '../../components/AIWorkspace/WorkspaceConfigDrawer';
import useWorkspaceConfig from '../../components/AIWorkspace/hooks/useWorkspaceConfig';
import useTransferRoulettes from '../../hooks/useTransferRoulettes';
import useOriginNumber from '../../hooks/useOriginNumber';
import useContactLabelsCount from '../../hooks/useContactLabelsCount';
import useContactAssignments from '../../hooks/useContactAssignments';
import useGestorInternal from '../../hooks/useGestorInternal';
import MessageText from '../../components/MessageText';
import RecordingBar from '../../components/RecordingBar';
import useAudioRecorder from '../../hooks/useAudioRecorder';
import ThreadEventCard from '../../components/chat/ThreadEventCard';
import ToolUsageCard from '../../components/chat/ToolUsageCard';
import AdContextCard, { adContextOf } from '../../components/chat/AdContextCard';
import AttachButton from '../../components/chat/AttachButton';
import MessagesSkeleton from '../../components/inbox/MessagesSkeleton';
import useThreadEvents from '../../hooks/useThreadEvents';

// Ações de mensagem (flag message_actions). Helpers puros, fora do
// componente, para serem usados pelos callbacks de SSE sem fechar sobre estado.

// Tipo de mídia (contrato do bloco `quoted` do backend) a partir do nó Baileys.
const mediaTypeOfMessage = (msg) => {
  const m = msg?.message || {};
  if (m.imageMessage) return 'image';
  if (m.videoMessage) return 'video';
  if (m.audioMessage) return 'audio';
  if (m.documentMessage) return 'document';
  if (m.stickerMessage) return 'sticker';
  return null;
};

// Grava o texto novo de uma edição no lugar certo do nó Baileys
// (conversation / extendedTextMessage.text / legenda da mídia) e carimba
// `edited`.
const withEditedText = (msg, text, editedAt = null) => {
  const m = { ...(msg?.message || {}) };
  if (m.extendedTextMessage) {
    m.extendedTextMessage = { ...m.extendedTextMessage, text };
  } else if (m.imageMessage) {
    m.imageMessage = { ...m.imageMessage, caption: text };
  } else if (m.videoMessage) {
    m.videoMessage = { ...m.videoMessage, caption: text };
  } else if (m.documentMessage) {
    m.documentMessage = { ...m.documentMessage, caption: text };
  } else {
    m.conversation = text;
  }
  return { ...msg, message: m, edited: true, ...(editedAt ? { editedAt } : {}) };
};

const ChatWindow = ({ chat, chats = [], onMessageSent, autopilotContacts, autopilotMode, blockedContacts, activeAgentName, onToggleAutopilot, onToggleBlockedContact, isHandoff = false, handoffMetadata = null, onResumeAgent, onContactNameUpdated, onContactDeleted, isMobile = false, onBack }) => {
  const { colors, theme } = useTheme();
  const { createNotification } = useNotifications();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  // Paginacao por scroll up — mesma logica do WA Business.
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const PAGE_SIZE = 30;
  const SCROLL_TOP_THRESHOLD = 80;
  const [hoveredMessage, setHoveredMessage] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);
  
  // Attached files state (from AI Workspace)
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showSavedDrawer, setShowSavedDrawer] = useState(false);
  const [showWorkspaceConfig, setShowWorkspaceConfig] = useState(false);
  const [scheduleVersion, setScheduleVersion] = useState(0);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [showLabelPicker, setShowLabelPicker] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showTagsManager, setShowTagsManager] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showContactDetails, setShowContactDetails] = useState(false);
  const [showCreateNotification, setShowCreateNotification] = useState(false);

  // Banner de handoff: o membro pode fechar (X) e não ver mais este aviso
  // (por contato + motivo). Persistido em localStorage; um NOVO motivo
  // (nova transferência) volta a mostrar.
  const [handoffBannerDismissed, setHandoffBannerDismissed] = useState(false);
  const _handoffPhoneKey = chat?._phoneNumber || whatsappService.extractNumber(chat?.remoteJid) || '';
  const _handoffReason = handoffMetadata?.handoff_reason || handoffMetadata?.reason || '';
  useEffect(() => {
    try {
      const map = JSON.parse(localStorage.getItem('handoffDismissed') || '{}');
      setHandoffBannerDismissed(map[_handoffPhoneKey] === (_handoffReason || '__'));
    } catch { setHandoffBannerDismissed(false); }
  }, [_handoffPhoneKey, _handoffReason]);
  const dismissHandoffBanner = () => {
    setHandoffBannerDismissed(true);
    try {
      const map = JSON.parse(localStorage.getItem('handoffDismissed') || '{}');
      map[_handoffPhoneKey] = _handoffReason || '__';
      localStorage.setItem('handoffDismissed', JSON.stringify(map));
    } catch { /* localStorage indisponível — só não persiste */ }
  };

  // Encaminhar mensagens: reusa a seleção do aiWorkspace (Chamar o Agente). O
  // ícone no header abre este modal com as mensagens já selecionadas.
  const [showForwardModal, setShowForwardModal] = useState(false);

  // Ações de mensagem (flag message_actions): responder citando, reagir,
  // editar a própria e encaminhar pelo menu da bolha + barra de seleção.
  // Com a flag desligada nada disto renderiza e o shell segue como antes.
  const { flags: featureFlags } = useFeatureFlags();
  const messageActionsOn = !!featureFlags?.message_actions;
  const messageCaps = useMemo(() => messageActionCaps('wa_personal'), []);
  // Mensagem escolhida em "Responder" (normalizada: id, externalId, text,
  // fromMe, mediaType, senderName).
  const [replyTo, setReplyTo] = useState(null);
  // Própria mensagem em edição ({ id, externalId, text }); o rascunho que
  // estava no campo volta ao cancelar.
  const [editing, setEditing] = useState(null);
  const draftBeforeEditRef = useRef('');

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  // O campo cresce com o texto tambem quando o valor entra por codigo ("Usar
  // resposta" do agente, Resposta Rapida, mensagem pronta): o onChange so roda
  // ao digitar, e um setTimeout no handler nao cobre o campo ser montado de
  // novo (o AI Workspace fecha e o compositor volta com 1 linha e barra de
  // rolagem). Caso do dono em 10/10/2026. O ref de callback cuida da montagem;
  // o efeito, de toda mudanca do valor.
  const ajustarAlturaDoInput = useCallback((el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 100) + 'px';
  }, []);
  const attachInputRef = useCallback((el) => {
    inputRef.current = el;
    ajustarAlturaDoInput(el);
  }, [ajustarAlturaDoInput]);
  useEffect(() => {
    ajustarAlturaDoInput(inputRef.current);
  }, [newMessage, ajustarAlturaDoInput]);
  const currentChatRef = useRef(null);

  // ✅ AI Workspace hook (with integrated history)
  const aiWorkspace = useAIWorkspace();
  const workspaceConfigState = useWorkspaceConfig();
  const { roulettes: transferRoulettes, assignToRoulette } = useTransferRoulettes();
  const [rouletteBusyId, setRouletteBusyId] = useState(null);
  const contactPhoneForLabels = chat?._phoneNumber || whatsappService.extractNumber(chat?.remoteJid);
  // Funcionalidade B — número de ORIGEM (por onde a mensagem sai). Vazio quando
  // o recurso está off/solo → chip e label não aparecem (comportamento atual).
  const origin = useOriginNumber(contactPhoneForLabels);
  const contactLabels = useContactLabelsCount(contactPhoneForLabels);
  // Responsável do contato (assigned_to). Membro humano em atendimento pausa
  // o agente para este contato (regra do agent_router no backend), mesmo com
  // o autopilot ligado — o badge e o toggle abaixo refletem isso.
  const { getAssignment } = useContactAssignments();
  const assignedTo = getAssignment(contactPhoneForLabels);
  // "Responder novos contatos" só para contatos novos: o backend diz se o
  // agente atende ESTE contato (contato antigo não mostra o agente).
  const agentAnswers = useAgentAnswers(contactPhoneForLabels, assignedTo?.assigned_at || 0);
  const assignedMember = assignedTo?.type === 'member' ? assignedTo : null;
  // Thread interno da Ponte do Gestor: o Gestor responde ali por desenho —
  // o cabeçalho troca o "Agente pausado" por um aviso próprio.
  const gestorInternal = useGestorInternal(contactPhoneForLabels);
  // Cards de sistema na thread (transferências). Releitura quando o
  // responsável muda ao vivo, para o card aparecer sem recarregar a página.
  const threadEvents = useThreadEvents(
    contactPhoneForLabels, assignedTo?.assigned_at || null
  );
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');
  
  // File upload state
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Gravacao de audio (voice note estilo WhatsApp)
  const audioRecorder = useAudioRecorder();
  const [sendingAudio, setSendingAudio] = useState(false);

  // Keep track of current chat for SSE callbacks
  useEffect(() => {
    currentChatRef.current = chat;
  }, [chat]);

  // Reset workspace and attached files when chat changes
  useEffect(() => {
    aiWorkspace.resetWorkspace();
    setNewMessage('');
    setAttachedFiles([]);
    audioRecorder.cancel();
    setShowForwardModal(false);
    setReplyTo(null);
    setEditing(null);
    draftBeforeEditRef.current = '';
  }, [chat?.remoteJid]);

  // ============================================
  // SSE EVENT HANDLERS
  // ============================================

  useSSEEvent(SSE_EVENTS.NEW_MESSAGE, (data) => {
    const currentChat = currentChatRef.current;
    if (!currentChat || !data) return;

    const messageJid = data.remoteJid;
    const rawJid = data.rawRemoteJid; // forma crua (ex.: @lid) — backend envia o canonico em remoteJid
    const altJid = data.key?.remoteJidAlt;

    // BUG do vazamento: as comparações de alt-jid casavam `undefined === undefined`
    // (o evento e o chat quase sempre não têm _altJid) => aceitava mensagem de
    // QUALQUER conversa da mesma instância na janela aberta. Agora TODA comparação
    // exige os dois lados definidos, e o telefone é o check confiável (dígitos).
    const _digits = (s) => String(s || '').replace(/\D/g, '');
    const curPhone = _digits(currentChat._phoneNumber || (currentChat.remoteJid || '').split('@')[0]);
    const msgPhone = _digits((messageJid || '').split('@')[0]);
    const isCurrentChat = (
      (!!messageJid && (messageJid === currentChat.remoteJid ||
        (!!currentChat._altJid && messageJid === currentChat._altJid))) ||
      (!!altJid && (altJid === currentChat.remoteJid ||
        (!!currentChat._altJid && altJid === currentChat._altJid))) ||
      (!!rawJid && (rawJid === currentChat.remoteJid ||
        (!!currentChat._altJid && rawJid === currentChat._altJid))) ||
      (!!curPhone && !!msgPhone && curPhone === msgPhone)
    );

    if (!isCurrentChat) return;

    // Colaboração em workspace compartilhado: o backend faz broadcast de cada
    // mensagem ENVIADA (fromMe) para todos os membros do workspace. Mas quem
    // ENVIOU já tem o eco otimista local — se reaplicássemos o broadcast aqui,
    // numa corrida (broadcast antes da resposta da API reconciliar o id temp)
    // o remetente veria a bolha duplicada. Por isso pulamos o nosso próprio
    // eco: comparamos `sentByUserId` com a identidade REAL do humano (o ator
    // em sessão delegada, ou o próprio usuário). Outros membros não têm esse
    // match → veem a mensagem aparecer (a percepção "alguém já está falando
    // com esse contato").
    if (data.fromMe && data.sentByUserId) {
      const delegation = getDelegation();
      const myRealId = delegation?.actorUserId || user?.id;
      if (data.sentByUserId === myRealId) return;
    }

    // Prefer the full Evolution-shaped payload from the backend when present —
    // it already contains `message.{audio|document|sticker|image|video}Message`
    // with the Azure Blob URL injected, so media renders immediately.
    // Fallback to the legacy shape built from flat SSE fields (text-only or
    // events emitted before the payload enrichment).
    const newMsg = data.messagePayload
      ? {
          // messagePayload já vem de to_evolution_shape e inclui `sentByName`
          // (atribuição de equipe) — o render da bolha mostra "Enviado por X".
          ...data.messagePayload,
          id: data.messagePayload.id || data.messageId || `sse-${Date.now()}`,
        }
      : {
          id: data.messageId || `sse-${Date.now()}`,
          key: data.key || {
            id: data.messageId,
            fromMe: data.fromMe,
            remoteJid: data.remoteJid,
          },
          message: {
            conversation: data.textContent,
          },
          messageType: data.messageType,
          messageTimestamp: data.timestamp || Math.floor(Date.now() / 1000),
          pushName: data.pushName,
          status: data.fromMe ? 'sent' : 'received',
          // Fallback (sem messagePayload): leva o nome do remetente para a
          // mesma bolha "Enviado por X".
          sentByName: data.sentByName || null,
        };

    setMessages(prev => {
      const exists = prev.some(m => 
        m.key?.id === newMsg.key?.id || 
        m.id === newMsg.id
      );
      
      if (exists) return prev;
      
      const updated = [...prev, newMsg];
      setTimeout(() => scrollToBottom(), 100);
      return updated;
    });

    if (!data.fromMe && data.messageId) {
      whatsappService.markAsRead(currentChat.remoteJid, data.messageId).catch(() => {});
    }
  }, []);

  useSSEEvent(SSE_EVENTS.MESSAGE_STATUS, (data) => {
    const currentChat = currentChatRef.current;
    if (!currentChat || !data) return;

    const messageJid = data.remoteJid;
    const isCurrentChat = (
      messageJid === currentChat.remoteJid ||
      messageJid === currentChat._altJid ||
      currentChat._phoneNumber === messageJid?.split('@')[0]
    );

    if (!isCurrentChat) return;

    setMessages(prev => prev.map(msg => {
      if (msg.key?.id === data.messageId) {
        return { ...msg, status: data.status };
      }
      return msg;
    }));
  }, []);

  // Reação: NÃO é mensagem nova — carimba o emoji na bolha alvo que já está na
  // thread. `reaction` null significa que o cliente removeu a reação.
  useSSEEvent(SSE_EVENTS.MESSAGE_REACTION, (data) => {
    const currentChat = currentChatRef.current;
    if (!currentChat || !data?.wamid) return;

    const messageJid = data.remoteJid;
    const isCurrentChat = (
      messageJid === currentChat.remoteJid ||
      messageJid === currentChat._altJid ||
      currentChat._phoneNumber === messageJid?.split('@')[0]
    );
    if (!isCurrentChat) return;

    // `field === 'reactionFromMe'` é a reação do OPERADOR (eco da nossa
    // própria reação, ou de outro membro); sem field é a do contato.
    const field = data.field === 'reactionFromMe' ? 'reactionFromMe' : 'reaction';
    setMessages(prev => prev.map(msg => (
      msg.key?.id === data.wamid ? { ...msg, [field]: data.reaction || null } : msg
    )));
  }, []);

  // Edição (WhatsApp QR): a bolha alvo ganha a marca "editada". Quando o
  // evento traz `text` (edição com texto novo, caminho das ações de
  // mensagem), o texto da bolha é trocado no lugar certo do nó Baileys.
  useSSEEvent(SSE_EVENTS.MESSAGE_EDITED, (data) => {
    if (!data?.wamid) return;
    const newText = typeof data.text === 'string' && data.text ? data.text : null;
    setMessages(prev => prev.map(msg => {
      if (msg.key?.id !== data.wamid) return msg;
      return newText
        ? withEditedText(msg, newText, data.editedAt || null)
        : { ...msg, edited: true, ...(data.editedAt ? { editedAt: data.editedAt } : {}) };
    }));
  }, []);

  // Mensagem apagada (pelo contato ou por nós): a bolha vira "Mensagem
  // apagada" e sai da seleção/encaminhamento.
  useSSEEvent(SSE_EVENTS.MESSAGE_DELETED, (data) => {
    if (!data?.wamid) return;
    setMessages(prev => prev.map(msg => (
      msg.key?.id === data.wamid ? { ...msg, deleted: true } : msg
    )));
  }, []);

  // ============================================
  // LOAD MESSAGES
  // ============================================

  // Ref do AbortController do fetch atual. Cancelado quando o operador
  // troca de chat antes da resposta chegar — evita race condition em que
  // mensagens do chat anterior aparecem brevemente no chat atual.
  const messagesAbortRef = useRef(null);
  // Frente C4 (flag chat_cache): remoteJid cuja lista de mensagens em estado
  // e a desta conversa (vinda do cache ou do fetch). O espelho estado->cache
  // so grava quando bate com o chat atual, para a troca de conversa nunca
  // gravar as mensagens de A na entrada de B.
  const cacheKeyRef = useRef(null);

  const loadMessages = useCallback(async () => {
    if (!chat?.remoteJid) return;

    // Cancela fetch em voo do chat anterior, se houver.
    if (messagesAbortRef.current) {
      messagesAbortRef.current.abort();
    }
    const controller = new AbortController();
    messagesAbortRef.current = controller;
    const requestedJid = chat.remoteJid;

    // Reset do paginador para o novo chat
    setHasMoreMessages(true);
    setLoadingMoreMessages(false);

    // SEMPRE busca o historico pelo remoteJid. ANTES pulava o fetch quando
    // chat._isNew — mas leads ANTIGOS abertos via busca/kanban (fora da pagina
    // carregada do inbox) vinham marcados _isNew e o historico (que EXISTE no
    // backend) nao carregava -> "Nenhuma mensagem ainda" num lead com conversa
    // (bug do historico migrado do Kommo). Para um numero genuinamente novo o
    // backend devolve [] e o estado vazio aparece normal (enviar a 1a msg segue
    // funcionando). Custo: 1 fetch a mais ao iniciar conversa nova — aceitavel.
    //
    // Frente C4: conversa ja vista pinta na hora com o cache e o fetch abaixo
    // revalida por baixo (sem skeleton). Sem a flag, skeleton como hoje.
    const cacheOn = getFeatureFlag('chat_cache');
    const cached = cacheOn ? getChatEntry('wa_personal', requestedJid) : null;
    cacheKeyRef.current = null;
    if (cached && Array.isArray(cached.messages)) {
      cacheKeyRef.current = requestedJid;
      setMessages(cached.messages);
      setHasMoreMessages(cached.messages.length >= PAGE_SIZE);
      setLoading(false);
      setTimeout(() => scrollToBottom(), 50);
    } else {
      setLoading(true);
    }

    try {
      const response = await whatsappService.getChatMessages(
        chat.remoteJid, PAGE_SIZE, { signal: controller.signal }
      );
      // Guard: troca de chat enquanto resposta voava? Descarta.
      if (requestedJid !== chat?.remoteJid) return;

      let loadedMessages = response.data || [];
      setHasMoreMessages(loadedMessages.length >= PAGE_SIZE);

      loadedMessages = [...loadedMessages].sort((a, b) => {
        const timestampA = Number(a.messageTimestamp) || 0;
        const timestampB = Number(b.messageTimestamp) || 0;
        return timestampA - timestampB;
      });

      if (cacheOn) {
        cacheKeyRef.current = requestedJid;
        setChatMessages('wa_personal', requestedJid, loadedMessages);
      }
      setMessages(loadedMessages);

      const lastReceivedMessage = [...loadedMessages]
        .reverse()
        .find(msg => msg.key && !msg.key.fromMe);

      if (lastReceivedMessage?.key?.id) {
        whatsappService.markAsRead(chat.remoteJid, lastReceivedMessage.key.id).catch(() => {});
      }

      setTimeout(() => scrollToBottom(), 100);

    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Error loading messages:', err);
      setMessages([]);
    } finally {
      if (messagesAbortRef.current === controller) {
        setLoading(false);
      }
    }
  }, [chat?.remoteJid, chat?._isNew]);

  useEffect(() => {
    if (chat?.remoteJid) {
      loadMessages();
    } else {
      cacheKeyRef.current = null;
      setMessages([]);
    }
    return () => {
      if (messagesAbortRef.current) {
        messagesAbortRef.current.abort();
        messagesAbortRef.current = null;
      }
    };
  }, [chat?.remoteJid, loadMessages]);

  // Frente C4: espelha o estado no cache (mensagem nova via SSE, status de
  // entrega, reacao, eco do envio). Declarado DEPOIS do efeito de carga para
  // a troca de conversa zerar cacheKeyRef antes de qualquer espelho.
  useEffect(() => {
    const key = chat?.remoteJid;
    if (!key || cacheKeyRef.current !== key) return;
    if (!getFeatureFlag('chat_cache')) return;
    setChatEntry('wa_personal', key, { messages });
  }, [messages, chat?.remoteJid]);

  useEffect(() => {
    if (chat && inputRef.current) {
      inputRef.current.focus();
    }
  }, [chat]);

  // ============================================
  // HELPERS
  // ============================================

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Pagina o historico para tras (mesmo padrao do WA Business): mede a
  // altura ANTES do prepend e ajusta scrollTop para manter o conteudo
  // estavel ao adicionar mensagens mais antigas.
  const loadMoreMessages = useCallback(async () => {
    if (!chat?.remoteJid || !hasMoreMessages || loadingMoreMessages || loading) return;
    const container = messagesContainerRef.current;
    if (!container) return;

    setLoadingMoreMessages(true);
    const previousScrollHeight = container.scrollHeight;
    const previousScrollTop = container.scrollTop;

    try {
      const response = await whatsappService.getChatMessages(
        chat.remoteJid, PAGE_SIZE, { offset: messages.length }
      );
      const older = response.data || [];
      if (older.length < PAGE_SIZE) setHasMoreMessages(false);
      if (older.length === 0) return;

      const sortedOlder = [...older].sort((a, b) => {
        const timestampA = Number(a.messageTimestamp) || 0;
        const timestampB = Number(b.messageTimestamp) || 0;
        return timestampA - timestampB;
      });

      // Dedup defensivo por key.id (SSE pode ter materializado novas durante o fetch).
      setMessages(prev => {
        const seen = new Set();
        prev.forEach(m => { if (m.key?.id) seen.add(m.key.id); });
        const filtered = sortedOlder.filter(m => {
          const key = m.key?.id;
          return key && !seen.has(key);
        });
        return [...filtered, ...prev];
      });

      requestAnimationFrame(() => {
        if (!messagesContainerRef.current) return;
        const newHeight = messagesContainerRef.current.scrollHeight;
        messagesContainerRef.current.scrollTop =
          previousScrollTop + (newHeight - previousScrollHeight);
      });
    } catch (err) {
      console.error('[WA Personal ChatWindow] loadMore error:', err);
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [chat?.remoteJid, hasMoreMessages, loadingMoreMessages, loading, messages.length]);

  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    setShowScrollButton(scrollHeight - scrollTop - clientHeight > 100);
    // Guards defensivos: nao paginar sem scroll possivel, sem mensagens
    // ainda carregadas, ou enquanto o load inicial nao terminou.
    if (scrollHeight <= clientHeight) return;
    if (messages.length === 0 || loading) return;
    if (scrollTop < SCROLL_TOP_THRESHOLD && hasMoreMessages && !loadingMoreMessages) {
      loadMoreMessages();
    }
  };

  // ============================================
  // SEND MESSAGE (with files support)
  // ============================================
  // ✅ OPTIMISTIC UI: Message appears immediately, input is freed instantly
  // API call runs in background - errors silently remove the message

  const handleSend = async (e, textOverride = null) => {
    e?.preventDefault();
    const messageText = textOverride || newMessage.trim();
    
    // Allow send if has text OR has files
    const hasText = messageText && messageText.length > 0;
    const hasFiles = attachedFiles.length > 0;
    
    if (!hasText && !hasFiles) return;
    if (sending) return;
    setSendError(null);

    // Editando a própria mensagem (flag message_actions): o botão de enviar
    // salva a edição em vez de mandar uma mensagem nova.
    if (messageActionsOn && editing && !textOverride) {
      if (!hasText) return;
      await handleSaveEdit(messageText);
      return;
    }

    // Responder citando: captura a citação junto com o texto e limpa a faixa
    // no mesmo passo otimista do campo (a bolha otimista já nasce com ela).
    const quotedFor = messageActionsOn ? replyTo : null;
    const quotedOpts = quotedFor
      ? { quotedMessageId: quotedFor.id || quotedFor.externalId }
      : {};

    // Capture files before clearing
    const filesToSend = [...attachedFiles];

    // ✅ OPTIMISTIC: Clear input and files IMMEDIATELY
    if (!textOverride) {
      setNewMessage('');
    }
    setAttachedFiles([]);
    if (quotedFor) setReplyTo(null);
    
    // ✅ Brief lock to prevent double-click, then release
    setSending(true);
    setTimeout(() => setSending(false), 300);

    // Create temp message for optimistic UI
    const tempId = `temp-${Date.now()}`;
    
    // Build proper message structure for media rendering
    let tempMessageContent = {};
    
    if (hasFiles && filesToSend.length > 0) {
      const firstFile = filesToSend[0];
      const fileType = firstFile.type || 'document';
      
      if (fileType === 'image') {
        tempMessageContent = {
          imageMessage: {
            caption: messageText || null,
            url: firstFile.url,
          }
        };
      } else if (fileType === 'video') {
        tempMessageContent = {
          videoMessage: {
            caption: messageText || null,
            url: firstFile.url,
          }
        };
      } else if (fileType === 'audio') {
        tempMessageContent = {
          audioMessage: {
            url: firstFile.url,
            seconds: 0,
          }
        };
      } else {
        tempMessageContent = {
          documentMessage: {
            fileName: firstFile.filename || 'Documento',
            mimetype: firstFile.mimeType || 'application/octet-stream',
            url: firstFile.url,
          }
        };
      }
      
      // Multiple files indicator
      if (filesToSend.length > 1) {
        tempMessageContent.conversation = messageText || `📎 +${filesToSend.length - 1} arquivo(s)`;
      }
    } else if (hasText) {
      tempMessageContent = { conversation: messageText };
    }
    
    const tempMessage = {
      id: tempId,
      key: { fromMe: true, id: tempId, remoteJid: chat.remoteJid },
      message: tempMessageContent,
      messageTimestamp: Math.floor(Date.now() / 1000),
      status: 'pending',
      _isTemp: true,
      _attachedFiles: filesToSend,
      ...(quotedFor ? { quoted: quotedFromMessage(quotedFor) } : {}),
    };
    
    // ✅ Add message to list IMMEDIATELY
    setMessages(prev => [...prev, tempMessage]);
    scrollToBottom();

    // ✅ API call runs in BACKGROUND - doesn't block UI
    try {
      // Prefer the phone already resolved by the backend (chat._phoneNumber).
      // Falling back to extractNumber(remoteJid) leaks LIDs for business
      // contacts (15-16 digit IDs under @s.whatsapp.net) which Evolution
      // rejects with `exists: false`.
      const number = chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid);
      
      console.log('[ChatWindow] Sending (background):', {
        number,
        textLength: messageText?.length || 0,
        filesCount: filesToSend.length,
      });
      
      let result;
      
      if (hasFiles) {
        result = await whatsappService.sendMessageWithMedia(
          number,
          messageText || null,
          filesToSend.map(f => ({
            url: f.url,
            type: f.type || 'document',
            filename: f.filename,
            mimeType: f.mimeType || null,
            // Escolha "Documento" no menu: precisa vencer o mimetype no
            // backend, senão um .mov volta a ser tratado como vídeo.
            asDocument: !!f.asDocument,
          })),
          quotedOpts
        );

        if (result.data?.partial) {
          console.warn('[ChatWindow] Partial success:', result.data.errors);
        }

        // Update with real ID (optional, improves status tracking)
        const realId = result.data?.text_message_id ||
                       result.data?.file_results?.[0]?.message_id;

        if (realId) {
          setMessages(prev => prev.map(m =>
            m.id === tempId
              ? { ...m, id: realId, key: { ...m.key, id: realId }, status: 'sent', _isTemp: false }
              : m
          ));
        }
      } else {
        result = await whatsappService.sendMessage(number, messageText, quotedOpts);

        const realId = result.data?.message_id;
        if (realId) {
          setMessages(prev => prev.map(m =>
            m.id === tempId
              ? { ...m, id: realId, key: { ...m.key, id: realId }, status: 'sent', _isTemp: false }
              : m
          ));
        }
      }

      // O backend retorna HTTP 200 com {success:false} quando NÃO consegue
      // resolver o destino (contato LID cujo telefone o WhatsApp ainda não
      // expôs). Sem tratar isso, a bolha ficava "enviando" para sempre.
      if (result && result.success === false) {
        setMessages(prev => prev.map(m =>
          m.id === tempId ? { ...m, status: 'failed', _isTemp: false } : m
        ));
        const motivo = result.error === 'invalid_phone_lid'
          ? 'Este contato ainda não pode receber mensagens pela plataforma — o WhatsApp não expôs o número dele. Assim que ele te enviar uma mensagem, o envio é liberado.'
          : (result.message || 'Não foi possível enviar a mensagem.');
        setSendError(motivo);
        return;
      }

      onMessageSent?.({ text: messageText, files: filesToSend });

    } catch (err) {
      // Erro de rede/exceção: marca a bolha como falha (não some em silêncio,
      // senão o usuário acha que enviou).
      console.error('[ChatWindow] Send failed:', err.message);
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, status: 'failed', _isTemp: false } : m
      ));
      // O backend manda {code, message} em português dizendo o que fazer (ex.:
      // conexão caída → reconectar). O texto genérico só entra quando não há
      // resposta nenhuma, que é o único caso em que o problema é mesmo a rede.
      const detail = err.response?.data?.detail;
      setSendError(
        detail?.message
        || (typeof detail === 'string' ? detail : null)
        || 'Falha ao enviar. Verifique a conexão e tente novamente.'
      );
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Escape no campo cancela a edição ou a resposta citando (flag
  // message_actions). Sem nada pendente não faz nada.
  const handleComposerKeyDown = (e) => {
    if (e.key !== 'Escape' || !messageActionsOn) return;
    if (editing) {
      e.preventDefault();
      cancelEditing();
    } else if (replyTo) {
      e.preventDefault();
      setReplyTo(null);
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  // Rotulo do dia: um so lugar para os 4 inboxes (paridade B3, 2026-09-17).
  const formatDate = (timestamp) => formatDayLabel(timestamp);

  // Texto de um templateMessage (mensagem de empresa com botões). Vem em
  // hydratedTemplate, fora da cadeia conversation/extendedText — sem isto a
  // mensagem tem texto real mas o balão renderiza vazio.
  const getTemplateText = (message) => {
    const t = message?.templateMessage?.hydratedTemplate
      || message?.templateMessage?.hydratedFourRowTemplate;
    if (!t) return '';
    return [t.hydratedTitleText, t.hydratedContentText, t.hydratedFooterText]
      .filter(Boolean)
      .join('\n\n');
  };

  // Texto "puro" da mensagem, qualquer que seja o envelope do Baileys.
  const getPlainText = (msg) => {
    const message = msg?.message || {};
    return (
      message.conversation ||
      message.extendedTextMessage?.text ||
      getTemplateText(message) ||
      ''
    );
  };

  // Descrição para os tipos que NÃO têm texto nem mídia (reação, localização,
  // contato, álbum, payload cifrado...). Sem isto o balão sai vazio — são ~5%
  // das mensagens do histórico. Nunca devolve vazio: tipo desconhecido cai no
  // rótulo genérico, que ainda é melhor que um balão em branco.
  const getPlaceholderContent = (msg) => {
    const message = msg?.message || {};

    if (message.reactionMessage) {
      const emoji = (message.reactionMessage.text || '').trim();
      return emoji ? `${emoji} Reagiu a uma mensagem` : 'Removeu a reação';
    }
    if (message.albumMessage) return 'Álbum de mídias';
    if (message.contactMessage || message.contactsArrayMessage) {
      const nome = message.contactMessage?.displayName;
      return nome ? `Contato: ${nome}` : 'Contato';
    }
    if (message.locationMessage || message.liveLocationMessage) return 'Localização';
    if (message.pollCreationMessage || message.pollCreationMessageV2
      || message.pollCreationMessageV3) return 'Enquete';
    if (message.pollUpdateMessage) return 'Resposta de enquete';
    if (message.pinInChatMessage) return 'Mensagem fixada';
    if (message.protocolMessage) return 'Mensagem apagada';
    return 'Mensagem não suportada';
  };

  const getMessageContent = (msg) => {
    const message = msg.message || {};

    if (message.reactionMessage) {
      return `${message.reactionMessage.text || '👍'} Reagiu a uma mensagem`;
    }

    return (
      getPlainText(msg) ||
      message.imageMessage?.caption ||
      message.videoMessage?.caption ||
      message.documentMessage?.fileName ||
      (message.imageMessage ? '📷 Imagem' : '') ||
      (message.videoMessage ? '🎥 Vídeo' : '') ||
      (message.audioMessage ? '🎵 Áudio' : '') ||
      (message.stickerMessage ? '🎨 Sticker' : '') ||
      (message.documentMessage ? '📄 Documento' : '') ||
      (message.contactMessage ? '👤 Contato' : '') ||
      (message.locationMessage ? '📍 Localização' : '') ||
      '[Mensagem]'
    );
  };

  const getMessageText = (msg) => {
    const message = msg.message || {};
    return (
      message.conversation ||
      message.extendedTextMessage?.text ||
      ''
    );
  };

  const isMessageBlocked = (msg) => {
    const message = msg?.message || {};
    // Mensagem apagada não entra na seleção nem no encaminhamento.
    if (messageActionsOn && (msg?.deleted || msg?.status === 'deleted')) return true;
    if (BLOCKED_MESSAGE_TYPES.some(type => message[type])) return true;

    const hasText = !!(message.conversation || message.extendedTextMessage?.text);
    const hasMediaUrl = !!(
      message.imageMessage?.url ||
      message.audioMessage?.url ||
      message.videoMessage?.url ||
      message.documentMessage?.url
    );
    return !hasText && !hasMediaUrl;
  };

  // Check if message has media content
  const hasMediaContent = (msg) => {
    const message = msg.message || {};
    return !!(
      message.imageMessage ||
      message.videoMessage ||
      message.audioMessage ||
      message.documentMessage ||
      message.stickerMessage
    );
  };

  // Check if message is text-only (no media)
  const isTextOnly = (msg) => !!getPlainText(msg) && !hasMediaContent(msg);

  const getMessageStatusIcon = (status) => {
    const iconStyle = { flexShrink: 0 };

    switch (status) {
      case 'read':
      case 'READ':
        return <CheckCheck size={13} style={{ ...iconStyle, color: '#53bdeb' }} />;
      case 'delivered':
      case 'DELIVERY_ACK':
        return <CheckCheck size={13} style={{ ...iconStyle, color: colors.messageTimeSent }} />;
      case 'sent':
      case 'SERVER_ACK':
        return <Check size={13} style={{ ...iconStyle, color: colors.messageTimeSent }} />;
      case 'pending':
        return <Loader2 size={12} style={{ ...iconStyle, color: colors.messageTimeSent, animation: 'spin 1s linear infinite' }} />;
      case 'failed':
      case 'error':
        return <AlertCircle size={13} style={{ ...iconStyle, color: '#ef4444' }} />;
      default:
        return null;
    }
  };

  // Texto curto do status (estilo Kommo) + cor. Failed em vermelho.
  const getStatusLabel = (status) => {
    // A PALAVRA vem do componente compartilhado (paridade B6, 2026-09-17);
    // a cor do tema e o layout do cabecalho continuam deste shell.
    const info = statusInfo(status, 'wa_personal');
    if (!info) return { text: '', color: colors.messageTimeSent };
    return { text: info.texto, color: info.cor || colors.messageTimeSent };
  };

  const getChatName = () => {
    // Mesmo filtro da lista (isUsableName): pushName que é número/dígito de
    // LID legado não pode aparecer cru no header enquanto a lista formata.
    const pick = (v) => (chatUtils.isUsableName(v) ? String(v).trim() : '');
    const name = pick(chat?.pushName) || pick(chat?.name) || pick(chat?.contactName);
    if (name) return name;
    const phone = chat?._phoneNumber || whatsappService.extractNumber(chat?.remoteJid);
    // LID sem telefone real: contato em sincronizacao (mesmo rotulo da lista).
    if (chatUtils.isLidIdentifier(phone) || chatUtils.isLidIdentifier(chat?.remoteJid)) {
      return chatUtils.SYNCING_CONTACT_LABEL;
    }
    return chatUtils.formatPhoneNumber(phone) || phone || 'Desconhecido';
  };

  const getChatAvatar = () => {
    if (chat?.profilePicUrl) {
      return <img src={chat.profilePicUrl} alt="" style={styles.avatarImg} />;
    }
    
    const isGroup = chat?._isGroup || whatsappService.isGroup(chat?.remoteJid);
    
    return (
      <div style={styles.avatarPlaceholder}>
        {isGroup ? <Users size={24} /> : <User size={24} />}
      </div>
    );
  };

  // ============================================
  // ATTACHED FILES HANDLERS
  // ============================================

  const handleRemoveAttachedFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearAttachedFiles = () => {
    setAttachedFiles([]);
  };

  // ============================================
  // MEDIA DOWNLOAD HANDLER
  // ============================================

  const handleDownloadMedia = useCallback(async (messageKey, mediaType) => {
    try {
      console.log('[ChatWindow] Downloading media:', messageKey, mediaType);
      const result = await whatsappService.downloadMediaFromMessage(messageKey, mediaType);
      return result;
    } catch (error) {
      console.error('[ChatWindow] Media download failed:', error);
      throw error;
    }
  }, []);

  // ============================================
  // FILE UPLOAD HANDLERS
  // ============================================

  // Anexo (clipe, menu, upload e compressão) vive no AttachButton, componente
  // compartilhado pelos quatro inboxes.
  const handleFilesUploaded = useCallback((files) => {
    setAttachedFiles(prev => [...prev, ...files]);
  }, []);

  const handleUploadError = useCallback((message) => {
    setUploadError(message);
    setTimeout(() => setUploadError(null), 5000);
  }, []);

  // ============================================
  // AUDIO RECORDING HANDLERS (voice note)
  // ============================================

  const handleStartRecording = async () => {
    if (sending || sendingAudio || uploading) return;
    try {
      await audioRecorder.start();
    } catch (err) {
      setUploadError(err.message || 'Nao foi possivel iniciar a gravacao.');
      setTimeout(() => setUploadError(null), 4000);
    }
  };

  const handleCancelRecording = () => {
    audioRecorder.cancel();
  };

  // Para a gravacao, faz upload do audio e envia como voice note via Evolution
  // (mesmo pipeline de midia, type 'audio').
  const handleStopAndSendAudio = async () => {
    if (sendingAudio) return;
    const file = await audioRecorder.stop();
    if (!file) return;

    setSendingAudio(true);
    setUploadError(null);
    setSendError(null);

    const tempId = `temp-${Date.now()}`;
    const localUrl = URL.createObjectURL(file);
    // Áudio também responde citando (flag message_actions), como no WhatsApp.
    const quotedFor = messageActionsOn ? replyTo : null;
    if (quotedFor) setReplyTo(null);
    const tempMessage = {
      id: tempId,
      key: { fromMe: true, id: tempId, remoteJid: chat.remoteJid },
      message: { audioMessage: { url: localUrl, seconds: 0 } },
      messageTimestamp: Math.floor(Date.now() / 1000),
      status: 'pending',
      _isTemp: true,
      ...(quotedFor ? { quoted: quotedFromMessage(quotedFor) } : {}),
    };
    setMessages(prev => [...prev, tempMessage]);
    scrollToBottom();

    try {
      const number = chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid);
      const uploaded = await fileUploadService.uploadFile(file, null, { channel: 'wa_personal' });
      const result = await whatsappService.sendMessageWithMedia(
        number,
        null,
        [{ url: uploaded.url, type: 'audio', filename: uploaded.filename, mimeType: uploaded.mimeType || file.type }],
        quotedFor ? { quotedMessageId: quotedFor.id || quotedFor.externalId } : {}
      );

      if (result && result.success === false) {
        setMessages(prev => prev.map(m =>
          m.id === tempId ? { ...m, status: 'failed', _isTemp: false } : m
        ));
        setSendError(result.message || 'Nao foi possivel enviar o audio.');
        return;
      }

      const realId = result.data?.file_results?.[0]?.message_id;
      setMessages(prev => prev.map(m =>
        m.id === tempId
          ? {
              ...m,
              id: realId || m.id,
              key: { ...m.key, id: realId || m.key.id },
              message: { audioMessage: { url: uploaded.url, seconds: 0 } },
              status: 'sent',
              _isTemp: false,
            }
          : m
      ));

      onMessageSent?.({ text: null, files: [uploaded] });
    } catch (err) {
      console.error('[ChatWindow] Audio send error:', err);
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, status: 'failed', _isTemp: false } : m
      ));
      setSendError('Falha ao enviar audio. Verifique a conexao e tente novamente.');
    } finally {
      setSendingAudio(false);
      try { URL.revokeObjectURL(localUrl); } catch (_) {}
    }
  };

  // ============================================
  // AI WORKSPACE HANDLERS
  // ============================================

  // Execute TRANSLATE (uses input text)
  // Execute TRANSLATE (uses input text)
  // ✅ Now stores originalText for regeneration
  const handleExecuteTranslate = async () => {
    if (!newMessage.trim()) return;

    const originalText = newMessage.trim();

    await aiWorkspace.executeTranslate(originalText, async ({ action, text, languages }) => {
      const results = await aiAgentService.processAction({
        action,
        text,
        languages,
      });
      
      // ✅ Store originalText in each result for regeneration
      return results.map(r => ({
        ...r,
        originalText: originalText,
      }));
    });
  };

  // Preview of selected messages for the "Chamar o Agente" modal context.
  // Recomputed only when the selection or messages change.
  const selectedMessagesPreview = useMemo(() => {
    if (aiWorkspace.selectedMessages.size === 0) return [];
    const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
    const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';
    const selectedMsgs = messages.filter(msg => {
      const msgId = msg.id || msg.key?.id;
      return aiWorkspace.selectedMessages.has(msgId);
    });
    const payload = extractMessagesPayload(selectedMsgs, {
      channel: 'wa_personal',
      chat,
      operatorName,
    });
    return payload.map(p => ({
      role: p.role,
      text: p.text,
      type: p.type,
      senderLabel: p.role === 'operator' ? 'Você' : (p.sender_name || 'Cliente'),
      timestamp: p.timestamp,
    }));
  }, [aiWorkspace.selectedMessages, messages, chat]);

  // Execute GENERATE (uses selected messages)
  // ✅ Now stores originalMessages for regeneration
  const handleExecuteGenerate = async () => {
    const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
    const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';

    const selectedMsgs = messages.filter(msg => {
      const msgId = msg.id || msg.key?.id;
      return aiWorkspace.selectedMessages.has(msgId);
    });

    const messagesPayload = extractMessagesPayload(selectedMsgs, {
      channel: 'wa_personal',
      chat,
      operatorName,
    });

    if (messagesPayload.length === 0) return;

    const contactPhone = chat?._phoneNumber
      || (chat?.remoteJid ? chat.remoteJid.split('@')[0] : '');

    const contextWithMessages = {
      chatId: chat?.remoteJid,
      chatName: getChatName(),
      contact_phone: contactPhone,
      channel: 'wa_personal',
      operatorName,
      originalMessages: messagesPayload,
    };

    await aiWorkspace.executeGenerate(messagesPayload, async ({ action, messages: msgs }) => {
      const results = await aiAgentService.processAction({
        action,
        messages: msgs,
        languages: [],
        context: contextWithMessages,
      });

      return results.map(r => ({
        ...r,
        originalMessages: messagesPayload,
      }));
    }, contextWithMessages);
  };

  // ✅ Use response (move to input + attach files)
  const handleUseResponse = (result) => {
    // Cards de MENSAGEM PRONTA do AI Workspace chegam no shape da API
    // (attachments/audio em snake_case), não no shape dos cards de IA
    // ({text, files}). Sem este desvio, os anexos e o áudio eram descartados
    // em silêncio — era o "mensagem pronta com áudio/arquivo não envia" do
    // celular (relato myCar 07/08), porque no mobile o caminho é o workspace.
    if (result && (Array.isArray(result.attachments) || result.audio)) {
      handleUseSavedMessage(result);
      return;
    }

    // Set text to input
    setNewMessage(result.text);

    // Attach files from result (if any)
    if (result.files && result.files.length > 0) {
      setAttachedFiles(prev => [...prev, ...result.files]);
      console.log('[ChatWindow] Attached files from AI response:', result.files.length, result.files);
    }

    inputRef.current?.focus();

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 100) + 'px';
      }
    }, 0);
  };

  // Insert a saved message (knowledge base) into the input/attachments, same
  // UX as "Usar resposta" for AI history cards.
  const handleUseSavedMessage = (item) => {
    if (!item) return;

    if (item.text) {
      // Variáveis já substituídas NO INPUT ({{primeiro_nome}}, saudação...),
      // editáveis antes do envio; o que o navegador não sabe (campo:X) fica
      // como marcador e o backend resolve no envio.
      setNewMessage(renderVariablesInText(item.text, {
        contactName: getChatName(),
        operatorName: user?.name,
        phone: chat?._phoneNumber,
      }));
    }

    // Convert saved-message attachment shape (snake_case) → attachedFiles shape
    // (camelCase) used by the chat send pipeline.
    const incoming = [];
    if (Array.isArray(item.attachments)) {
      for (const a of item.attachments) {
        if (!a?.file_url) continue;
        incoming.push({
          url: a.file_url,
          filename: a.file_name || 'arquivo',
          type: a.type || 'file',
          mimeType: a.mime_type,
          size: a.file_size,
        });
      }
    }
    if (item.audio?.file_url) {
      incoming.push({
        url: item.audio.file_url,
        filename: item.audio.file_name || 'audio.webm',
        type: 'audio',
        mimeType: item.audio.mime_type || 'audio/webm',
        size: undefined,
      });
    }

    if (incoming.length > 0) {
      setAttachedFiles((prev) => [...prev, ...incoming]);
    }

    inputRef.current?.focus();
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 100) + 'px';
      }
    }, 0);
  };

  // Regenerate translate result
  // ✅ Now uses originalText stored in the result
  const handleRegenerateTranslate = async (result) => {
    if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.TRANSLATE) <= 0) return;
    
    setRegeneratingId(result.id);
    
    try {
      // ✅ Use original text from the result
      const originalText = result.originalText || result.text;
      
      await aiWorkspace.regenerateResult(
        result.id, 
        ACTION_TYPES.TRANSLATE,
        async (params) => {
          const results = await aiAgentService.processAction({
            ...params,
            text: originalText, // ✅ Use original text
          });
          
          // ✅ Preserve originalText in regenerated result
          return results.map(r => ({
            ...r,
            originalText: originalText,
          }));
        },
        { text: originalText, originalText }
      );
    } finally {
      setRegeneratingId(null);
    }
  };

  // Regenerate generate result
  // ✅ Now uses originalMessages stored in the result
  const handleRegenerateGenerate = async (result) => {
    if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.GENERATE) <= 0) return;

    setRegeneratingId(result.id);

    try {
      const originalMessages = result.originalMessages || [];

      if (originalMessages.length === 0) {
        console.warn('[ChatWindow] No original messages found for regeneration');
        return;
      }

      const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
      const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';
      const contactPhone = chat?._phoneNumber
        || (chat?.remoteJid ? chat.remoteJid.split('@')[0] : '');

      await aiWorkspace.regenerateResult(
        result.id,
        ACTION_TYPES.GENERATE,
        async (params) => {
          const results = await aiAgentService.processAction({
            ...params,
            messages: originalMessages,
            context: {
              chatId: chat?.remoteJid,
              chatName: getChatName(),
              contact_phone: contactPhone,
              channel: 'wa_personal',
              operatorName,
            },
          });

          return results.map(r => ({
            ...r,
            originalMessages: originalMessages,
          }));
        },
        { originalMessages }
      );
    } finally {
      setRegeneratingId(null);
    }
  };

  // Execute CUSTOM prompt
  // Execute CUSTOM prompt
  // ✅ Now stores originalPrompt for regeneration
  const handleExecuteCustom = async (prompt, files = []) => {
    if (!prompt?.trim()) return;

    const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
    const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';
    const originalPrompt = prompt.trim();
    const originalFiles = Array.isArray(files) ? files : [];

    const selectedMsgs = messages.filter(msg => {
      const msgId = msg.id || msg.key?.id;
      return aiWorkspace.selectedMessages.has(msgId);
    });
    const refMessages = extractMessagesPayload(selectedMsgs, {
      channel: 'wa_personal',
      chat,
      operatorName,
    });

    const contactPhone = chat?._phoneNumber
      || (chat?.remoteJid ? chat.remoteJid.split('@')[0] : '');

    const baseContext = {
      userId: userMetadata.id || 'anonymous',
      chatId: chat?.remoteJid,
      chatName: getChatName(),
      contact_phone: contactPhone,
      channel: 'wa_personal',
      operatorName,
      messages: refMessages,
    };

    await aiWorkspace.executeCustom(originalPrompt, async ({ action, prompt: userPrompt }) => {
      const results = await aiAgentService.processAction({
        action,
        prompt: userPrompt,
        files: originalFiles,
        context: baseContext,
      });

      return results.map(r => ({
        ...r,
        originalPrompt: originalPrompt,
        originalFiles: originalFiles,
      }));
    }, baseContext);
  };

  // Roleta de transferencia: distribui o contato atual pelo grupo escolhido
  // (chip do AI Workspace). O header de responsavel atualiza sozinho via SSE
  // CONTACT_ASSIGNMENT_CHANGED; o spinner no chip dura ate o assign responder.
  const handleExecuteRoulette = async (groupId) => {
    const phone = contactPhoneForLabels;
    if (!phone || rouletteBusyId) return;
    setRouletteBusyId(groupId);
    try {
      await assignToRoulette(phone, groupId);
    } catch (err) {
      console.error('[ChatWindow] roleta:', err);
    } finally {
      setRouletteBusyId(null);
    }
  };

  // Regenerate custom result
  // ✅ Now uses originalPrompt stored in the result
  const handleRegenerateCustom = async (result) => {
    if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.CUSTOM) <= 0) return;
    
    setRegeneratingId(result.id);
    
    try {
      const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
      
      // ✅ Use original prompt from the result
      const originalPrompt = result.originalPrompt || '';
      
      if (!originalPrompt) {
        console.warn('[ChatWindow] No original prompt found for regeneration');
        return;
      }
      
      const operatorName = userMetadata.name || userMetadata.full_name || 'Operador';
      const contactPhone = chat?._phoneNumber
        || (chat?.remoteJid ? chat.remoteJid.split('@')[0] : '');

      await aiWorkspace.regenerateResult(
        result.id,
        ACTION_TYPES.CUSTOM,
        async (params) => {
          const results = await aiAgentService.processAction({
            ...params,
            prompt: originalPrompt,
            context: {
              userId: userMetadata.id || 'anonymous',
              chatId: chat?.remoteJid,
              chatName: getChatName(),
              contact_phone: contactPhone,
              channel: 'wa_personal',
              operatorName,
            },
          });
          
          // ✅ Preserve originalPrompt in regenerated result
          return results.map(r => ({
            ...r,
            originalPrompt: originalPrompt,
          }));
        },
        { originalPrompt }
      );
    } finally {
      setRegeneratingId(null);
    }
  };

  // Handle message selection with validation
  const handleToggleMessageSelection = (msgId, msg) => {
    const isBlocked = isMessageBlocked(msg);
    const isAtLimit = aiWorkspace.selectedMessages.size >= MAX_SELECTED_MESSAGES;
    const isCurrentlySelected = aiWorkspace.selectedMessages.has(msgId);
    
    if (isCurrentlySelected) {
      aiWorkspace.toggleMessageSelection(msgId, msg);
      return;
    }
    
    if (isBlocked || isAtLimit) {
      return;
    }
    
    aiWorkspace.toggleMessageSelection(msgId, msg);
  };

  // ============================================
  // AÇÕES DE MENSAGEM (flag message_actions)
  // Responder citando, reagir, editar a própria, copiar, selecionar e
  // encaminhar pelo menu da bolha. Só renderizam com a flag ligada.
  // ============================================

  const chatNumber = () => chat?._phoneNumber || whatsappService.extractNumber(chat?.remoteJid);

  const errorMessageOf = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    return detail?.message
      || (typeof detail === 'string' ? detail : null)
      || err?.message
      || fallback;
  };

  // Normaliza a mensagem (shape Baileys) para o contrato das ações:
  // { id, externalId, text, fromMe, mediaType, senderName }.
  const normalizeForAction = (msg, senderName = null) => {
    const m = msg?.message || {};
    const fromMe = !!msg?.key?.fromMe;
    return {
      id: msg?.id || null,
      externalId: msg?.key?.id || null,
      text: getPlainText(msg)
        || m.imageMessage?.caption
        || m.videoMessage?.caption
        || m.documentMessage?.caption
        || '',
      fromMe,
      mediaType: mediaTypeOfMessage(msg),
      senderName: fromMe
        ? (senderName || user?.name || null)
        : (getChatName() || msg?.pushName || null),
    };
  };

  const focusComposer = () => {
    setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 100) + 'px';
    }, 0);
  };

  // Cancela a edição e devolve ao campo o rascunho que estava lá.
  const cancelEditing = () => {
    if (!editing) return;
    setEditing(null);
    setNewMessage(draftBeforeEditRef.current || '');
    draftBeforeEditRef.current = '';
    focusComposer();
  };

  const startReply = (msg, senderName) => {
    if (editing) cancelEditing();
    setReplyTo(normalizeForAction(msg, senderName));
    focusComposer();
  };

  const startEdit = (msg) => {
    const normalized = normalizeForAction(msg);
    if (!editing) draftBeforeEditRef.current = newMessage;
    setReplyTo(null);
    setEditing({ id: normalized.id, externalId: normalized.externalId, text: normalized.text });
    setNewMessage(normalized.text);
    focusComposer();
  };

  // Salva a edição (chamado pelo handleSend enquanto `editing` está ativo).
  const handleSaveEdit = async (text) => {
    const target = editing;
    if (!target) return;
    const newText = (text || '').trim();
    if (!newText) return;
    if (newText === (target.text || '').trim()) {
      cancelEditing();
      return;
    }
    setSending(true);
    try {
      const result = await whatsappService.editMessage(
        target.id || target.externalId, newText, chatNumber()
      );
      if (result && result.success === false) {
        setSendError(result.message || 'Não foi possível editar a mensagem.');
        return;
      }
      const editedAt = result?.data?.edited_at || new Date().toISOString();
      setMessages(prev => prev.map(m => (
        (target.id && m.id === target.id) || (target.externalId && m.key?.id === target.externalId)
          ? withEditedText(m, newText, editedAt)
          : m
      )));
      setEditing(null);
      setNewMessage('');
      draftBeforeEditRef.current = '';
    } catch (err) {
      setSendError(errorMessageOf(err, 'Não foi possível editar a mensagem. Verifique a conexão e tente novamente.'));
    } finally {
      setSending(false);
    }
  };

  // Reage (emoji) ou remove a reação (null) na mensagem. Otimista: o chip
  // aparece na hora e volta ao estado anterior se o envio falhar.
  const react = async (msg, emoji) => {
    const id = msg?.id || msg?.key?.id;
    if (!id) return;
    const previous = msg.reactionFromMe || null;
    const next = emoji || null;
    const apply = (value) => setMessages(prev => prev.map(m => (
      (m.id === id || m.key?.id === id) ? { ...m, reactionFromMe: value } : m
    )));
    apply(next);
    try {
      const result = await whatsappService.sendReaction(id, next, chatNumber());
      if (result && result.success === false) {
        throw new Error(result.message || 'Não foi possível reagir à mensagem.');
      }
    } catch (err) {
      apply(previous);
      setSendError(errorMessageOf(err, 'Não foi possível reagir à mensagem.'));
    }
  };

  const copyMessageText = async (msg) => {
    const { text } = normalizeForAction(msg);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      setSendError('Não foi possível copiar o texto.');
    }
  };

  // Clicar na citação rola até a mensagem original e a destaca por 2 s.
  const jumpToMessage = (quoted) => {
    if (!quoted) return;
    const target = messages.find((m) => (
      (quoted.id && m.id === quoted.id)
      || (quoted.external_id && m.key?.id === quoted.external_id)
    ));
    const domId = target ? (target.id || target.key?.id) : (quoted.id || quoted.external_id);
    const el = domId ? document.getElementById(`msg-${domId}`) : null;
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('msg-row--flash');
    setTimeout(() => el.classList.remove('msg-row--flash'), 2000);
  };

  // "Encaminhar" no menu da bolha: a seleção vira só esta mensagem e o modal
  // de destinos abre direto. toggleMessageSelection SEM a mensagem pula a
  // checagem de closure (canSelectMessage) e lê a seleção já limpa; o
  // bloqueio por tipo é checado aqui.
  const forwardSingleMessage = (msgId, msg) => {
    if (isMessageBlocked(msg)) return;
    const current = aiWorkspace.selectedMessages;
    if (!(current.size === 1 && current.has(msgId))) {
      aiWorkspace.clearSelection();
      aiWorkspace.toggleMessageSelection(msgId);
    }
    setShowForwardModal(true);
  };

  // ============================================
  // FORWARD (Encaminhar) — seleção própria + reenvio por canal
  // ============================================

  // Extrai o que reenviar de uma mensagem (shape Baileys). Texto vira
  // { text }; mídia vira { file: {url,type,filename,mimeType}, caption }.
  // Retorna null quando não há nada reenviável (sem texto e sem URL de mídia —
  // ex.: mídia ainda não baixada do Evolution, que não tem URL pública).
  const extractForwardable = (msg) => {
    const m = msg?.message || {};

    const text =
      m.conversation ||
      m.extendedTextMessage?.text ||
      '';

    const mediaSpecs = [
      { node: m.imageMessage, type: 'image' },
      { node: m.videoMessage, type: 'video' },
      { node: m.audioMessage, type: 'audio' },
      { node: m.documentMessage, type: 'document' },
    ];

    for (const { node, type } of mediaSpecs) {
      if (node && node.url) {
        return {
          file: {
            url: node.url,
            type,
            filename: node.fileName || `${type}_${Date.now()}`,
            mimeType: node.mimetype || null,
          },
          caption: node.caption || '',
        };
      }
    }

    if (text && text.trim()) {
      return { text: text.trim() };
    }

    return null;
  };

  // Destinos: reaproveita a lista de chats já carregada no Inbox. Pula grupos
  // e pseudo-chats sem telefone real (LID em sincronização).
  const forwardDestinations = useMemo(() => {
    const seen = new Set();
    const out = [];
    (chats || []).forEach((c) => {
      const isGroup = c._isGroup || whatsappService.isGroup(c.remoteJid);
      if (isGroup) return;
      const phone = c._phoneNumber || whatsappService.extractNumber(c.remoteJid);
      const digits = (phone || '').replace(/\D/g, '');
      if (!digits || chatUtils.isLidIdentifier(phone) || chatUtils.isLidIdentifier(c.remoteJid)) {
        return; // sem telefone real — não dá para enviar
      }
      if (seen.has(digits)) return;
      seen.add(digits);
      out.push({
        id: c.remoteJid || digits,
        name: chatUtils.getChatDisplayName(c),
        phone: chatUtils.formatPhoneNumber(digits) || digits,
        _number: digits,
        avatarUrl: c.profilePicUrl || null,
      });
    });
    return out;
  }, [chats]);

  // Reenvia, para cada destino, cada mensagem selecionada (na ordem em que
  // aparecem). Erros são por destino — uma falha não aborta os demais.
  const handleForwardSend = async (selectedDestinations) => {
    // Mensagens selecionadas (MESMA seleção do aiWorkspace), na ordem do chat.
    const toForward = messages
      .filter((msg) => aiWorkspace.selectedMessages.has(msg.id || msg.key?.id))
      .map((msg) => extractForwardable(msg))
      .filter(Boolean);

    const outcomes = [];
    for (const dest of selectedDestinations) {
      const number = dest._number;
      let ok = true;
      try {
        for (const item of toForward) {
          if (item.text) {
            await whatsappService.sendMessage(number, item.text);
          } else if (item.file) {
            await whatsappService.sendMessageWithMedia(
              number,
              item.caption || null,
              [item.file]
            );
          }
        }
      } catch (err) {
        console.error('[ChatWindow] Forward failed for', dest.name, err);
        ok = false;
      }
      outcomes.push({ id: dest.id, name: dest.name, ok });
    }

    return outcomes;
  };

  // Linha do tempo: mensagens + cards de sistema (transferências) no MESMO
  // eixo de timestamp, agrupados por data. Os eventos anteriores à primeira
  // mensagem carregada ficam de fora — senão a paginação por scroll faria o
  // card aparecer acima de uma conversa que ainda nem foi buscada.
  //
  // Reações antigas: até o fix do webhook, cada reação era gravada como uma
  // MENSAGEM própria. Aqui elas são dobradas na leitura — o emoji vira chip da
  // bolha alvo (mesmo resultado do caminho novo, que grava `reaction` no doc)
  // e a mensagem-reação sai da thread. Sem backfill: vale para todo o
  // histórico já gravado.
  const { groupedMessages, foldedReactions } = useMemo(() => {
    const wamids = new Set(messages.map((m) => m.key?.id).filter(Boolean));

    // Alvo -> emoji, na ordem cronológica: a última reação vence, e texto
    // vazio (o cliente removeu a reação) limpa o chip.
    const reactions = {};
    const folded = new Set();
    [...messages]
      .sort((a, b) => (a.messageTimestamp || 0) - (b.messageTimestamp || 0))
      .forEach((m) => {
        const r = m.message?.reactionMessage;
        const targetId = r?.key?.id;
        // Alvo fora da janela carregada: não dobra, senão a reação sumiria
        // sem deixar rastro. Nesse caso a mensagem-reação continua na thread
        // com a descrição ("<emoji> Reagiu a uma mensagem").
        if (!r || !targetId || !wamids.has(targetId)) return;
        reactions[targetId] = (r.text || '').trim() || null;
        folded.add(m.id || m.key?.id);
      });

    const visible = messages.filter((m) => !folded.has(m.id || m.key?.id));
    const firstTs = visible.length
      ? Math.min(...visible.map((m) => m.messageTimestamp || Infinity))
      : null;
    const timeline = [
      ...visible.map((m) => ({ item: m, ts: m.messageTimestamp || 0 })),
      ...threadEvents
        .filter((e) => firstTs === null || e.ts >= firstTs)
        .map((e) => ({ item: e, ts: e.ts })),
    ].sort((a, b) => a.ts - b.ts);

    const groups = timeline.reduce((acc, { item, ts }) => {
      const date = formatDate(ts);
      if (!acc[date]) acc[date] = [];
      acc[date].push(item);
      return acc;
    }, {});

    return { groupedMessages: groups, foldedReactions: reactions };
  }, [messages, threadEvents]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // STYLES
  // ============================================

  const styles = {
    container: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      position: 'relative',
      // Superficie base do chat. A faixa do AIWorkspace (wrapper transparente)
      // herda esta cor, ficando igual ao header e ao fundo do ChatList.
      backgroundColor: colors.bgPrimary,
    },
    emptyContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgSecondary,
    },
    emptyContent: {
      textAlign: 'center',
      color: colors.textSecondary,
    },
    emptyTitle: {
      fontSize: '32px',
      fontWeight: 300,
      color: colors.textPrimary,
      marginBottom: '12px',
    },
    emptyText: {
      fontSize: '14px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: isMobile ? '4px' : '8px',
      padding: isMobile ? '8px 8px' : '10px 16px',
      // iOS PWA (standalone): empurra o conteudo do header para BAIXO da status
      // bar (wifi/horario/bateria) para o botao de voltar nao ficar inacessivel
      // sob a barra. env() = 0 em telas sem notch/desktop, entao e seguro sempre.
      ...(isMobile && { paddingTop: 'calc(8px + env(safe-area-inset-top))' }),
      // Mesma cor do fundo do ChatList (branco/navy), sem o cinza do headerBg.
      backgroundColor: colors.bgPrimary,
      // Borda dura trocada por uma sombra bem leve para separar do conteudo.
      boxShadow: colors.shadowSm,
      minHeight: '60px',
      zIndex: 1,
    },
    headerInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    headerAvatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      overflow: 'hidden',
    },
    avatarImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    avatarPlaceholder: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgTertiary,
      color: colors.textTertiary,
    },
    headerDetails: {
      display: 'flex',
      flexDirection: 'column',
    },
    headerName: {
      fontWeight: 500,
      fontSize: '16px',
      color: colors.textPrimary,
      // No mobile o nome trunca em vez de empurrar/espremer o header.
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: isMobile ? '46vw' : 'none',
    },
    headerStatus: {
      fontSize: '13px',
      color: colors.textSecondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '6px' : '8px',
      flexShrink: 0,
    },
    headerActionBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '50%',
      cursor: 'pointer',
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgChat,
    },
    messagesLoading: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
    },
    messagesEmpty: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
    },
    messageGroup: {
      marginBottom: '16px',
    },
    dateDivider: {
      display: 'flex',
      justifyContent: 'center',
      margin: '16px 0',
    },
    dateDividerText: {
      backgroundColor: colors.bgTertiary,
      padding: '6px 14px',
      borderRadius: '8px',
      fontSize: '12px',
      color: colors.textSecondary,
      boxShadow: colors.shadowSm,
    },
    messageRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginBottom: '2px',
      padding: '2px 0',
      borderRadius: '8px',
      transition: 'background-color 0.15s ease',
    },
    messageRowSelectedSent: {
      backgroundColor: `${workspaceColors.roleOperator}15`,
      margin: '2px -8px',
      padding: '6px 8px',
      borderRight: `3px solid ${workspaceColors.roleOperator}`,
    },
    messageRowSelectedReceived: {
      backgroundColor: `${workspaceColors.roleCustomer}15`,
      margin: '2px -8px',
      padding: '6px 8px',
      borderLeft: `3px solid ${workspaceColors.roleCustomer}`,
    },
    messageBubbleWrapper: {
      flex: 1,
      display: 'flex',
      position: 'relative',
    },
    messageSent: {
      justifyContent: 'flex-end',
    },
    messageReceived: {
      justifyContent: 'flex-start',
    },
    messageContentSent: {
      maxWidth: '65%',
      padding: '8px 12px',
      borderRadius: '8px',
      backgroundColor: colors.messageSent,
      color: colors.messageSentText,
      borderTopRightRadius: '2px',
    },
    messageContentReceived: {
      maxWidth: '65%',
      padding: '8px 12px',
      borderRadius: '8px',
      backgroundColor: colors.messageReceived,
      color: colors.messageReceivedText,
      borderTopLeftRadius: '2px',
    },
    messageText: {
      fontSize: '14px',
      lineHeight: 1.4,
      // overflowWrap: 'anywhere' garante quebra mesmo em URLs/strings longas
      // sem espacos, evitando o scroll horizontal feio no balao.
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
      margin: 0,
      minWidth: 0,
    },
    // Emoji de reação preso à mensagem, no canto de baixo do balão (estilo
    // WhatsApp). Fundo do próprio chat para "descolar" do balão.
    messageEdited: {
      fontSize: '10px',
      fontStyle: 'italic',
      color: 'var(--text-muted, #94a3b8)',
      marginRight: '6px',
    },
    messageReaction: {
      display: 'inline-block',
      marginTop: '4px',
      padding: '1px 6px',
      borderRadius: '10px',
      fontSize: '13px',
      lineHeight: 1.3,
      backgroundColor: colors.bgTertiary,
      boxShadow: colors.shadowSm,
    },
    // Descrição de mensagem sem texto (reação, localização, contato...):
    // mesmo balão, em itálico e discreto — não é texto que o cliente digitou.
    messagePlaceholder: {
      fontSize: '14px',
      lineHeight: 1.4,
      fontStyle: 'italic',
      opacity: 0.85,
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
      margin: 0,
      minWidth: 0,
    },
    senderHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      fontSize: '11px',
      fontWeight: 400,
      color: colors.messageTimeSent,
      marginBottom: '4px',
      maxWidth: '100%',
    },
    messageMeta: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '4px',
      marginTop: '4px',
    },
    messageTimeSent: {
      fontSize: '11px',
      color: colors.messageTimeSent,
    },
    messageTimeReceived: {
      fontSize: '11px',
      color: colors.textTertiary,
    },
    messageMedia: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '200px',
      height: '150px',
      backgroundColor: colors.bgTertiary,
      borderRadius: '8px',
      marginBottom: '8px',
      color: colors.textTertiary,
    },
    messageDocument: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px',
      backgroundColor: colors.bgTertiary,
      borderRadius: '8px',
      marginBottom: '8px',
    },
    scrollButton: {
      position: 'absolute',
      bottom: '80px',
      right: '24px',
      width: '42px',
      height: '42px',
      borderRadius: '50%',
      backgroundColor: colors.bgPrimary,
      border: 'none',
      boxShadow: colors.shadowMd,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
      zIndex: 10,
    },
    inputContainer: {
      // Mesma superficie do header (bgPrimary). O campo em si usa bgTertiary
      // abaixo para nao sumir contra esse fundo.
      backgroundColor: colors.bgPrimary,
    },
    // Input padronizado com o TextAudioInput do onboarding: bordas
    // arredondadas, anexo e enviar circulares dentro do mesmo container.
    inputForm: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 12px 12px',
      // iOS PWA (standalone): levanta o input acima do home indicator para nao
      // colar na borda inferior nem deixar a "margem preta" sob a barra do Safari.
      ...(isMobile && { paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }),
    },
    inputWrapper: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      borderRadius: '24px',
      border: `1px solid ${colors.inputBorder}`,
      // Mesma cor interna do search do ChatList (bgTertiary) para destacar do
      // fundo bgPrimary do container e nao "sumir".
      backgroundColor: colors.bgTertiary,
      padding: '4px 6px',
      transition: 'border-color 0.2s ease',
    },
    inputActionBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '34px',
      height: '34px',
      minWidth: '34px',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '50%',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'background-color 0.15s ease',
    },
    textarea: {
      flex: 1,
      border: 'none',
      background: 'transparent',
      padding: '6px 4px',
      // 16px no mobile evita o zoom automático do iOS ao focar o compositor.
      fontSize: isMobile ? '16px' : '14px',
      color: colors.textPrimary,
      resize: 'none',
      outline: 'none',
      maxHeight: '100px',
      lineHeight: 1.4,
      fontFamily: 'inherit',
    },
    sendBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '34px',
      height: '34px',
      minWidth: '34px',
      border: 'none',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      borderRadius: '50%',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'opacity 0.15s ease',
    },
    sendBtnDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };

  // ============================================
  // RENDER
  // ============================================

  if (!chat) {
    return (
      <div style={styles.emptyContainer}>
        <div style={styles.emptyContent}>
          <h3 style={styles.emptyTitle}>InfinitSales</h3>
          <p style={styles.emptyText}>Selecione uma conversa para começar a enviar mensagens</p>
        </div>
      </div>
    );
  }

  const canSend = (newMessage.trim() || attachedFiles.length > 0) && !uploading;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        {/* Mobile: botão voltar (←) abre a lista de novo, tela cheia estilo WhatsApp. */}
        {isMobile && onBack && (
          <button
            type="button"
            onClick={onBack}
            title="Voltar"
            style={{
              ...styles.headerActionBtn,
              color: colors.textPrimary,
              flexShrink: 0,
              marginRight: 0,
            }}
          >
            <ArrowLeft size={24} />
          </button>
        )}
        {(() => {
          const isGroup = chat._isGroup || whatsappService.isGroup(chat.remoteJid);
          const clickable = !isGroup;
          return (
            <button
              type="button"
              onClick={clickable ? () => setShowContactDetails(true) : undefined}
              disabled={!clickable}
              title={clickable ? 'Ver dados do contato' : ''}
              style={{
                ...styles.headerInfo,
                background: 'transparent', border: 'none',
                padding: '2px 4px', borderRadius: 8,
                cursor: clickable ? 'pointer' : 'default',
                textAlign: 'left',
                // No mobile ocupa o espaço livre e deixa o nome truncar.
                flex: isMobile ? 1 : undefined,
                minWidth: isMobile ? 0 : undefined,
              }}
            >
              <div style={styles.headerAvatar}>{getChatAvatar()}</div>
              <div style={{ ...styles.headerDetails, minWidth: isMobile ? 0 : undefined }}>
                <span style={styles.headerName}>{getChatName()}</span>
                <span style={styles.headerStatus}>
                  {isGroup
                    ? 'Grupo'
                    : chatUtils.formatPhoneNumber(
                        chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid)
                      )}
                </span>
                {(() => {
                  // Thread interno da Ponte do Gestor: quem responde aqui é o
                  // Gestor (para a equipe), não o atendimento de lead — dizer
                  // "Agente pausado" enquanto ele conversa era informação
                  // errada (item 12 da manutenção de 05/08).
                  if (gestorInternal) {
                    return (
                      <span style={{ fontSize: '11px', color: colors.accentPrimary, display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Bot size={10} />
                        Gestor ativo para a equipe interna
                      </span>
                    );
                  }
                  if (assignedMember) {
                    return (
                      <span style={{ fontSize: '11px', color: '#d97706', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Bot size={10} />
                        {`Em atendimento com ${assignedMember.name || 'membro da equipe'}`}
                      </span>
                    );
                  }
                  const chatPhone = chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid);
                  const isActive = autopilotMode === 'all'
                    ? !(blockedContacts || []).some(c => c.replace(/\D/g, '') === chatPhone?.replace(/\D/g, ''))
                    : (autopilotContacts || []).includes(chatPhone);
                  // Responsável agente específico prevalece sobre o principal
                  // (mesma resolução do agent_router).
                  const agentName = (assignedTo?.type === 'agent' && assignedTo.name) || activeAgentName;
                  return isActive && agentAnswers && agentName ? (
                    <span style={{ fontSize: '11px', color: colors.accentPrimary, opacity: 0.85, display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <Bot size={10} />
                      {agentName}
                    </span>
                  ) : null;
                })()}
              </div>
            </button>
          );
        })()}
        <div style={styles.headerActions}>
          {/* Controle de agente por contato movido para o Transferir contato
              (membro / agente / ninguem). "Transferir para ninguem" pausa o
              agente; transferir para um agente reativa. Fonte unica = assigned_to
              (ver agent_router / TransferContactModal) — sem botao dedicado. */}
          <RequiredFieldsSignal
            contactPhone={chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid)}
            colors={colors}
            refreshKey={contactLabels.count}
            onClick={() => setShowContactDetails(true)}
          />
          <RequiredFieldsPrompt
            contactKey={chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid)}
            colors={colors}
          />
          <FollowupBadge
            contactPhone={chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid)}
            colors={colors}
            refreshKey={scheduleVersion}
            onNavigateToFollowup={(id) => window.open(`/followups?id=${id}`, '_blank')}
          />
          {/* Encaminhar: aparece quando há mensagens selecionadas (a MESMA
              seleção do aiWorkspace/Chamar o Agente) e abre o modal de destinos.
              Com a flag message_actions a ação mora na barra de seleção,
              no lugar do composer (o cliente não achava este ícone). */}
          {!messageActionsOn && aiWorkspace.selectedMessages.size > 0 && (
            <button
              onClick={() => setShowForwardModal(true)}
              style={styles.headerActionBtn}
              title={`Encaminhar ${aiWorkspace.selectedMessages.size} ${aiWorkspace.selectedMessages.size === 1 ? 'mensagem' : 'mensagens'}`}
            >
              <Forward size={20} />
            </button>
          )}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowHeaderMenu(!showHeaderMenu)}
              style={styles.headerActionBtn}
              title="Mais opções"
            >
              <MoreVertical size={20} />
            </button>
            {showHeaderMenu && (
              <>
                <div
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
                  onClick={() => setShowHeaderMenu(false)}
                />
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                  backgroundColor: colors.bgPrimary, borderRadius: '10px',
                  border: `1px solid ${colors.borderColor}`,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  zIndex: 100, minWidth: '210px', overflow: 'hidden',
                  padding: '6px 0',
                }}>
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowSavedDrawer(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', padding: '10px 14px', border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textPrimary,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <History size={15} color={colors.accentPrimary} />
                    Mensagens Prontas
                  </button>
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowScheduleModal(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', padding: '10px 14px', border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textPrimary,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <Zap size={15} color={colors.accentPrimary} />
                    Agendar Follow-up
                  </button>
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowLabelPicker(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', padding: '10px 14px', border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textPrimary,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <Tags size={15} color={colors.accentPrimary} />
                    {contactLabels.hasLabels ? 'Alterar funil/etapa' : 'Definir funil/etapa'}
                  </button>
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowTransferModal(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', padding: '10px 14px', border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textPrimary,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <UserCheck size={15} color="#f59e0b" />
                    Transferir contato
                  </button>
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowFeedbackModal(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', padding: '10px 14px', border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textPrimary,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <MessageSquarePlus size={15} color="#10B981" />
                    Adicionar comentário
                  </button>
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowCreateNotification(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', padding: '10px 14px', border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textPrimary,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <Bell size={15} color={colors.accentPrimary} />
                    Criar notificação
                  </button>
                  <div style={{ height: 1, backgroundColor: colors.borderColor, margin: '6px 14px' }} />
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowWorkspaceConfig(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', padding: '10px 14px', border: 'none',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textPrimary,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgSecondary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <Settings size={15} color={colors.textSecondary} />
                    Configurar Workspace
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Lead unificado nos 3 canais (flag lead_multichannel): "Também no
          Instagram" com botão para abrir a conversa do outro canal. Mesmo
          componente nos 4 inboxes; grupo não tem outro canal. */}
      {!(chat._isGroup || whatsappService.isGroup(chat.remoteJid)) && (
        <ContactChannelChips variant="bar" contactKey={chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid)} channel="wa_personal" colors={colors} />
      )}

      {/* Handoff Banner — fundo levemente azulado (não cor de pele),
          botões filled no padrão dos CTAs da plataforma. */}
      {isHandoff && !handoffBannerDismissed && (
        <HandoffBanner
          theme={theme}
          colors={colors}
          agentName={handoffMetadata?.handoff_agent_name || activeAgentName}
          reason={handoffMetadata?.handoff_reason || handoffMetadata?.reason}
          // Handoff sem destino vai para um humano (admin ou o proprio
          // usuario): o evento chega antes da atribuicao, entao o nome vem
          // do responsavel quando o metadata nao traz.
          targetName={handoffMetadata?.handoff_target_name || assignedMember?.name}
          onAddComment={() => setShowFeedbackModal(true)}
          onResume={() => onResumeAgent?.(chat._phoneNumber || chatUtils.extractPhone(chat.remoteJid))}
          onDismiss={dismissHandoffBanner}
        />
      )}

      {/* Schedule Message Modal (drawer no chat) */}
      <ScheduleMessageModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        contactPhone={chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid)}
        contactName={chat.pushName || chat.name || chat.contactName || ''}
        colors={colors}
        channel="wa_personal"
        onScheduled={() => setScheduleVersion(v => v + 1)}
        displayMode="drawer"
      />

      {/* Label Picker Modal (drawer no chat) */}
      <LabelPickerModal
        isOpen={showLabelPicker}
        onClose={() => { setShowLabelPicker(false); contactLabels.reload(); }}
        contactPhone={chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid)}
        contactName={chat.pushName || chat.name || chat.contactName || ''}
        colors={colors}
        displayMode="drawer"
      />

      {/* Gerenciar tags do contato (múltiplas, criar na hora) */}
      <ContactTagsManagerModal
        isOpen={showTagsManager}
        onClose={() => setShowTagsManager(false)}
        contactPhone={chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid)}
        contactName={chat.pushName || chat.name || chat.contactName || ''}
        colors={colors}
        displayMode="drawer"
      />

      {/* Transferência de responsável (drawer no chat) */}
      <TransferContactModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        contactPhone={chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid)}
        contactName={chat.pushName || chat.name || chat.contactName || ''}
        colors={colors}
        displayMode="drawer"
      />


      {/* Contact Feedback Modal (drawer no chat) */}
      <ContactFeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        contactPhone={chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid)}
        contactName={chat.pushName || chat.name || chat.contactName || ''}
        colors={colors}
        displayMode="drawer"
      />

      {/* Mensagens Prontas — drawer lateral com mensagens salvas. No QR Code
          nao temos templates Meta, entao a aba so aparece em WA Business. */}
      <SavedMessagesDrawer
        isOpen={showSavedDrawer}
        onClose={() => setShowSavedDrawer(false)}
        colors={colors}
        channel="wa_personal"
        contactPhone={chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid)}
        contactName={chat.pushName || chat.name || chat.contactName || ''}
        onUseMessage={handleUseSavedMessage}
      />

      {/* Configurar Workspace — toggles das tags do AI Workspace. */}
      <WorkspaceConfigDrawer
        isOpen={showWorkspaceConfig}
        onClose={() => setShowWorkspaceConfig(false)}
        colors={colors}
        config={workspaceConfigState.config}
        onToggle={workspaceConfigState.toggle}
        transferRoulettes={transferRoulettes}
      />

      {/* Criar notificação interna ligada ao contato atual (menu do chat) */}
      <CreateNotificationModal
        isOpen={showCreateNotification}
        onClose={() => setShowCreateNotification(false)}
        contactName={getChatName()}
        onSubmit={async ({ title, body, scheduled_at }) => {
          await createNotification({
            title,
            body,
            scheduled_at,
            contact_phone: contactPhoneForLabels || null,
            chat_id: chat?.remoteJid || chat?.id || null,
            channel: 'wa_personal',
            show_toast: false,
          });
        }}
      />

      {/* Drawer "Dados do contato" — clique no avatar/nome no header.
          Envolto em ErrorBoundary: se o drawer falhar, o ChatWindow continua
          vivo (volta, lê e responde o chat) em vez de cair na tela preta. */}
      <ErrorBoundary
        name="contact-drawer"
        resetKeys={[showContactDetails, chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid)]}
        fallback={({ reset }) => (showContactDetails ? (
          <DrawerErrorFallback
            colors={colors}
            onClose={() => { reset(); setShowContactDetails(false); }}
          />
        ) : null)}
      >
        <ContactDetailsDrawer
          isOpen={showContactDetails}
          onClose={() => setShowContactDetails(false)}
          colors={colors}
          source="wa_personal"
          channel="wa_personal"
          contactKey={chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid)}
          companyContactId={chat.company_contact_id || null}
          contactPhone={chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid)}
          contactName={getChatName()}
          contactProfilePicUrl={chat.profilePicUrl}
          onNameUpdated={(newName) => {
            onContactNameUpdated?.({
              remoteJid: chat.remoteJid,
              phone: chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid),
              newName,
            });
          }}
          onNavigateToFollowup={(id) => window.open(`/followups?id=${id}`, '_blank')}
          onDeleted={(phone) => onContactDeleted?.(phone)}
        />
      </ErrorBoundary>

      {/* Messages */}
      <div
        style={styles.messagesContainer}
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {loading && messages.length === 0 ? (
          // Primeira carga da conversa: skeleton de bolhas no lugar do spinner
          // central (componente compartilhado pelos 4 inboxes).
          <MessagesSkeleton />
        ) : loading ? (
          <div style={styles.messagesLoading}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
            <span>Carregando mensagens...</span>
          </div>
        ) : messages.length === 0 ? (
          <div style={styles.messagesEmpty}>
            <p style={{ fontSize: '16px', marginBottom: '4px' }}>Nenhuma mensagem ainda</p>
            <span style={{ fontSize: '14px', color: colors.textTertiary }}>
              Envie uma mensagem para iniciar a conversa
            </span>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, msgs]) => (
            <div key={date} style={styles.messageGroup}>
              <div style={styles.dateDivider}>
                <span style={styles.dateDividerText}>{date}</span>
              </div>
              {msgs.map((msg, index) => {
                // Card de sistema (transferência): entra na mesma lista para
                // respeitar a ordem cronológica, mas não é balão de mensagem.
                if (msg.__threadEvent) {
                  return (
                    <ThreadEventCard key={msg.id} event={msg.event} colors={colors} />
                  );
                }

                // Observabilidade de tools: doc tool_event é card de sistema.
                // (messageTimestamp em segundos → ms para o horário do card.)
                if (msg.toolEvent || msg.messageType === 'tool_event') {
                  return (
                    <ToolUsageCard
                      key={msg.id || msg.key?.id || `toolev-${index}`}
                      toolEvent={msg.toolEvent}
                      agentName={msg.agentName}
                      timestamp={msg.messageTimestamp ? msg.messageTimestamp * 1000 : null}
                      colors={colors}
                    />
                  );
                }

                const isSent = msg.key?.fromMe;
                // Remetente (estilo Kommo): nome curto de quem enviou. Agente
                // mostra o nome do agente; humano (membro/admin via workspace)
                // mostra sentByName; o próprio dono mostra o próprio nome.
                // Follow-up gerado pela IA (source "followup") traz agentId: é
                // mensagem do agente, com o nome dele e o ícone de robô (2026-09-16).
                const isAgentMsg = msg.source === 'agent_autopilot' || msg.agent_response || msg.originatedByAgent || !!msg.agentId;
                // Thread unificada: mensagem espelhada de OUTRA conexão da empresa
                // mostra o remetente de ORIGEM (ex.: "enviado por João"), não o
                // dono deste workspace.
                // A autoria vem da PRÓPRIA mensagem (agentName) — numa conta com
                // vários agentes, activeAgentName é o agente PRINCIPAL do
                // workspace e rotularia toda bolha automática com o mesmo nome,
                // mesmo depois da transferência. Fica só como último recurso,
                // para as mensagens antigas gravadas sem autoria.
                const senderName = isSent
                  ? (isAgentMsg
                      ? (msg.agentName || msg.sentByName || activeAgentName || msg.sourceConnectionName || 'Agente IA')
                      : (msg.isMirror
                          ? (msg.originatedByName || msg.sentByName || msg.sourceConnectionName || null)
                          : (msg.sentByName || user?.name || null)))
                  : null;
                const senderShort = senderName ? senderName.split(' ')[0] : null;
                // Selo "via {conexão}" para mensagens espelhadas — deixa claro que
                // a conversa veio de outra conexão conectada ao mesmo lead.
                const mirrorVia = msg.isMirror ? (msg.sourceConnectionName || null) : null;
                const msgId = msg.id || msg.key?.id || `msg-${index}`;
                const isSelected = aiWorkspace.selectedMessages.has(msgId);
                const isHovered = hoveredMessage === msgId;
                const showCheckbox = isHovered || isSelected || aiWorkspace.selectedMessages.size > 0;
                const msgIsBlocked = isMessageBlocked(msg);
                const isAtLimit = aiWorkspace.selectedMessages.size >= MAX_SELECTED_MESSAGES;
                // Balão sem texto e sem mídia: descreve o tipo (reação,
                // localização, contato...) em vez de renderizar vazio.
                const msgPlaceholder = (!isTextOnly(msg) && !hasMediaContent(msg))
                  ? getPlaceholderContent(msg)
                  : null;
                const msgReaction = msg.reaction || foldedReactions[msg.key?.id] || null;

                const selectedRowStyle = isSelected
                  ? (isSent ? styles.messageRowSelectedSent : styles.messageRowSelectedReceived)
                  : {};
                const contactFirstName = (getChatName() || 'Cliente').split(' ')[0];

                // Ações de mensagem (flag message_actions): o que o menu da
                // bolha pode fazer com ESTA mensagem.
                const isDeleted = messageActionsOn && !!(msg.deleted || msg.status === 'deleted');
                const actionText = messageActionsOn ? normalizeForAction(msg, senderName).text : '';
                const actionHasMedia = messageActionsOn && hasMediaContent(msg);
                const editReason = messageActionsOn
                  ? whyCannotEdit({
                      fromMe: !!isSent,
                      hasText: !!actionText,
                      hasMedia: actionHasMedia,
                      timestampMs: msg.messageTimestamp ? Number(msg.messageTimestamp) * 1000 : null,
                      deleted: isDeleted,
                    }, messageCaps)
                  : '';
                const forwardReason = messageActionsOn && msgIsBlocked
                  ? (isDeleted ? 'Mensagem apagada' : 'Esta mensagem não pode ser encaminhada')
                  : '';

                return (
                  <div
                    key={msgId}
                    id={messageActionsOn ? `msg-${msg.id || msg.key?.id}` : undefined}
                    className={messageActionsOn ? `msg-row${isSelected ? ' msg-row--selected' : ''}` : undefined}
                    style={{
                      ...styles.messageRow,
                      ...selectedRowStyle,
                    }}
                    onMouseEnter={() => setHoveredMessage(msgId)}
                    onMouseLeave={() => setHoveredMessage(null)}
                  >
                    {/* Checkbox de seleção (aiWorkspace): seleciona mensagens
                        para o agente E para encaminhar (ícone no header). */}
                    <MessageCheckbox
                      isSelected={isSelected}
                      isVisible={showCheckbox}
                      onToggle={() => handleToggleMessageSelection(msgId, msg)}
                      isBlocked={msgIsBlocked}
                      isAtLimit={isAtLimit && !isSelected}
                      blockReason={msgIsBlocked ? 'Apenas mensagens de texto' : ''}
                    />

                    {/* Message bubble */}
                    <div
                      style={{
                        ...styles.messageBubbleWrapper,
                        ...(isSent ? styles.messageSent : styles.messageReceived),
                      }}
                    >
                      {isSelected && (
                        <MessageRolePill
                          role={isSent ? 'operator' : 'customer'}
                          label={isSent ? 'Sua mensagem' : contactFirstName}
                          side={isSent ? 'right' : 'left'}
                        />
                      )}

                      {/* Coluna bolha + chips de reação (flag message_actions).
                          Sem a flag é `display: contents`: some do layout e a
                          bolha fica como sempre foi. */}
                      <div style={messageActionsOn
                        ? {
                            display: 'flex', flexDirection: 'column', minWidth: 0, maxWidth: '65%',
                            alignItems: isSent ? 'flex-end' : 'flex-start',
                          }
                        : { display: 'contents' }}
                      >
                      <div style={{
                        ...(isSent ? styles.messageContentSent : styles.messageContentReceived),
                        ...(messageActionsOn ? { position: 'relative', maxWidth: '100%' } : {}),
                      }}>
                        {messageActionsOn && !isDeleted && (
                          <MessageActionMenu
                            fromMe={!!isSent}
                            colors={colors}
                            caps={messageCaps}
                            editReason={editReason}
                            hasText={!!actionText}
                            currentReaction={msg.reactionFromMe || null}
                            forwardReason={forwardReason}
                            alwaysVisible={isMobile}
                            onReply={() => startReply(msg, senderName)}
                            onReact={(emoji) => react(msg, emoji)}
                            onForward={() => forwardSingleMessage(msgId, msg)}
                            onSelect={() => handleToggleMessageSelection(msgId, msg)}
                            onCopy={() => copyMessageText(msg)}
                            onEdit={() => startEdit(msg)}
                          />
                        )}
                        {/* Cabeçalho estilo Kommo (bolha enviada): horário,
                            nome do remetente, ícone e texto de status numa
                            linha só, em cinza discreto (sem negrito). */}
                        {isSent && (() => {
                          const st = getStatusLabel(msg.status);
                          return (
                            <div style={styles.senderHeader}>
                              <span>{formatTime(msg.messageTimestamp)}</span>
                              {isAgentMsg && <Bot size={11} style={{ flexShrink: 0 }} />}
                              {senderShort && (
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {senderShort}
                                </span>
                              )}
                              {/* Saiu por OUTRO número (origem ≠ a conexão deste
                                  workspace): confirma "· pelo nº de X". */}
                              {mirrorVia && (
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {`· pelo nº de ${mirrorVia.split(' ')[0]}`}
                                </span>
                              )}
                              {getMessageStatusIcon(msg.status)}
                              {st.text && <span style={{ color: st.color }}>{st.text}</span>}
                            </div>
                          );
                        })()}

                        {/* Citação ("responder em cima"): clicar rola até a
                            mensagem original. */}
                        {messageActionsOn && !isDeleted && msg.quoted && (
                          <QuotedMessage
                            quoted={msg.quoted}
                            contactName={getChatName()}
                            colors={colors}
                            fromMe={!!isSent}
                            onJump={jumpToMessage}
                          />
                        )}

                        {/* Anúncio que trouxe o lead (INBOX_AD_CARD_ENABLED) */}
                        {adContextOf(msg) && !isSent && (
                          <AdContextCard ad={adContextOf(msg)} colors={colors} />
                        )}

                        {/* Mensagem apagada (flag message_actions): só o aviso,
                            sem mídia nem citação. O placeholder antigo do
                            protocolMessage segue valendo abaixo. */}
                        {isDeleted ? (
                          <p style={{ ...styles.messagePlaceholder, opacity: 0.7 }}>Mensagem apagada</p>
                        ) : (
                          <>
                            {/* Media Content (images, audio, video, documents) */}
                            {hasMediaContent(msg) && (
                              <MessageMedia
                                message={msg}
                                isSent={isSent}
                                onDownload={handleDownloadMedia}
                              />
                            )}

                            {/* Text Content (only show if text-only or has caption) */}
                            {isTextOnly(msg) && (
                              <p style={styles.messageText}><MessageText text={getMessageContent(msg)} /></p>
                            )}

                            {/* Sem texto e sem mídia (reação, localização, contato,
                                álbum...): descreve o tipo em vez de deixar o balão
                                vazio. */}
                            {msgPlaceholder && (
                              <p style={styles.messagePlaceholder}>{msgPlaceholder}</p>
                            )}
                          </>
                        )}

                        {/* Reação NESTA mensagem. Vem do campo `reaction` do
                            doc (caminho novo) ou da dobra em leitura das
                            reações antigas, gravadas como mensagem. */}
                        {msg.edited && !isDeleted && (
                          <span
                            style={styles.messageEdited}
                            title={messageActionsOn
                              ? (isSent ? 'Mensagem editada' : 'O contato editou esta mensagem no WhatsApp')
                              : 'O contato editou esta mensagem no WhatsApp. O texto novo não chega até nós: confirme com ele.'}
                          >
                            editada
                          </span>
                        )}
                        {!messageActionsOn && msgReaction && (
                          <span style={styles.messageReaction}>{msgReaction}</span>
                        )}

                        {/* Recebidas mantêm o horário discreto embaixo; as
                            enviadas levam tudo no cabeçalho (acima). */}
                        {!isSent && (
                          <div style={styles.messageMeta}>
                            <span style={styles.messageTimeReceived}>
                              {formatTime(msg.messageTimestamp)}
                            </span>
                            {mirrorVia && (
                              <span style={styles.messageTimeReceived}>
                                {` · no nº de ${mirrorVia.split(' ')[0]}`}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      {/* Chips de reação (contato + operador) embaixo da
                          bolha; clicar no seu troca ou remove. */}
                      {messageActionsOn && !isDeleted && (
                        <ReactionChips
                          reaction={msgReaction}
                          reactionFromMe={msg.reactionFromMe || null}
                          align={isSent ? 'right' : 'left'}
                          colors={colors}
                          onChangeMine={messageCaps.reactions ? (emoji) => react(msg, emoji) : undefined}
                        />
                      )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll Button */}
      {showScrollButton && (
        <button style={styles.scrollButton} onClick={scrollToBottom}>
          <ArrowDown size={20} />
        </button>
      )}

      {/* Modal de Encaminhar (abre pelo ícone no header quando há mensagens
          selecionadas — a MESMA seleção do aiWorkspace) */}
      <ForwardModal
        isOpen={showForwardModal}
        onClose={() => setShowForwardModal(false)}
        messageCount={aiWorkspace.selectedMessages.size}
        destinations={forwardDestinations}
        onForward={handleForwardSend}
        colors={colors}
      />

      {/* AI Workspace */}
      <AIWorkspace
        isOpen={aiWorkspace.isWorkspaceOpen}
        onToggleWorkspace={aiWorkspace.toggleWorkspace}
        selectedCount={aiWorkspace.selectedMessages.size}
        inputText={newMessage}
        
        // Dropdown
        activeDropdown={aiWorkspace.activeDropdown}
        onToggleDropdown={aiWorkspace.toggleDropdown}
        onCloseDropdown={aiWorkspace.closeDropdown}
        
        // Translate
        translateLanguages={aiWorkspace.translateLanguages}
        onToggleTranslateLanguage={aiWorkspace.toggleTranslateLanguage}
        translateResults={aiWorkspace.translateResults}
        translateProcessing={aiWorkspace.translateProcessing}
        translateError={aiWorkspace.translateError}
        hasTranslateResults={aiWorkspace.hasTranslateResults}
        
        // Generate (no longer needs language selection)
        generateResults={aiWorkspace.generateResults}
        generateProcessing={aiWorkspace.generateProcessing}
        generateError={aiWorkspace.generateError}
        hasGenerateResults={aiWorkspace.hasGenerateResults}
        
        // Custom Prompt
        customResults={aiWorkspace.customResults}
        customProcessing={aiWorkspace.customProcessing}
        customError={aiWorkspace.customError}
        hasCustomResults={aiWorkspace.hasCustomResults}
        
        // ✅ History - using integrated aiWorkspace
        historyItems={aiWorkspace.historyItems}
        isHistoryOpen={aiWorkspace.isHistoryOpen}
        onToggleHistory={aiWorkspace.toggleHistory}
        onRemoveFromHistory={aiWorkspace.removeFromHistory}
        onClearHistory={aiWorkspace.clearHistory}
        hasHistory={aiWorkspace.hasHistory}

        // Config das tags + handlers compartilhados com o menu de 3 pontinhos.
        // O drawer real (mensagens salvas, etiquetas, etc) e renderizado abaixo;
        // o workspace so dispara a abertura via callback.
        workspaceConfig={workspaceConfigState.config}
        onOpenSavedMessages={() => setShowSavedDrawer(true)}
        onOpenLabels={() => setShowLabelPicker(true)}
        onOpenFollowup={() => setShowScheduleModal(true)}
        onOpenFeedback={() => setShowFeedbackModal(true)}
        onOpenWorkspaceConfig={() => setShowWorkspaceConfig(true)}

        // Actions
        onClear={aiWorkspace.clearSelection}
        onExecuteTranslate={handleExecuteTranslate}
        onExecuteGenerate={handleExecuteGenerate}
        onExecuteCustom={handleExecuteCustom}
        onUseResponse={handleUseResponse}
        onRegenerateTranslate={handleRegenerateTranslate}
        onRegenerateGenerate={handleRegenerateGenerate}
        onRegenerateCustom={handleRegenerateCustom}
        onRemoveTranslateResult={aiWorkspace.removeTranslateResult}
        onRemoveGenerateResult={aiWorkspace.removeGenerateResult}
        onRemoveCustomResult={aiWorkspace.removeCustomResult}
        
        // Regeneration
        getTranslateRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.TRANSLATE)}
        getGenerateRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.GENERATE)}
        getCustomRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.CUSTOM)}
        regeneratingId={regeneratingId}

        // Context preview for "Chamar o Agente" modal
        selectedMessagesPreview={selectedMessagesPreview}

        // Roletas de transferencia (grupos do admin) — chips dinamicos
        transferRoulettes={transferRoulettes}
        onExecuteRoulette={handleExecuteRoulette}
        rouletteProcessingId={rouletteBusyId}

        // Funcionalidade B — número de ORIGEM. Só mostramos no inbox pessoal as
        // origens deste canal (wa_personal); o backend roteia o envio físico.
        originOptions={(origin.options || []).filter((o) => o.channel === 'wa_personal')}
        activeOrigin={origin.activeOrigin?.channel === 'wa_personal' ? origin.activeOrigin : null}
        onSelectOrigin={origin.setOrigin}
        allowSwitchOrigin={origin.canSwitch}
      />

      {/* Barra de seleção (flag message_actions): com mensagens selecionadas
          ela toma o lugar do composer, como no WhatsApp, com Encaminhar e
          Chamar o Agente à vista. */}
      {messageActionsOn && aiWorkspace.selectedMessages.size > 0 ? (
        <div style={styles.inputContainer}>
          <SelectionBar
            count={aiWorkspace.selectedMessages.size}
            max={MAX_SELECTED_MESSAGES}
            colors={colors}
            onForward={() => setShowForwardModal(true)}
            onAskAgent={aiWorkspace.openWorkspace}
            onCancel={aiWorkspace.clearSelection}
          />
        </div>
      ) : (
      <div style={styles.inputContainer}>
        {/* Send Error (ex.: contato LID cujo telefone o WhatsApp não expôs) */}
        {sendError && (
          <div style={{
            padding: '8px 16px',
            backgroundColor: `${colors.danger || '#ef4444'}15`,
            borderTop: `1px solid ${colors.danger || '#ef4444'}30`,
            color: colors.danger || '#ef4444',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}>
            <span>⚠️ {sendError}</span>
            <button
              onClick={() => setSendError(null)}
              style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 14 }}
            >✕</button>
          </div>
        )}
        {/* Upload Error Message */}
        {uploadError && (
          <div style={{
            padding: '8px 16px',
            backgroundColor: `${colors.danger || '#ef4444'}15`,
            borderTop: `1px solid ${colors.danger || '#ef4444'}30`,
            color: colors.danger || '#ef4444',
            fontSize: '12px',
          }}>
            ⚠️ {uploadError}
          </div>
        )}
        
        {/* Faixa de "Respondendo a" / "Editando mensagem" (flag message_actions) */}
        {messageActionsOn && editing && (
          <ReplyPreview
            mode="edit"
            quoted={quotedFromMessage({
              id: editing.id, externalId: editing.externalId, text: editing.text, fromMe: true,
            })}
            contactName={getChatName()}
            colors={colors}
            onCancel={cancelEditing}
          />
        )}
        {messageActionsOn && !editing && replyTo && (
          <ReplyPreview
            mode="reply"
            quoted={quotedFromMessage(replyTo)}
            contactName={getChatName()}
            colors={colors}
            onCancel={() => setReplyTo(null)}
          />
        )}

        {/* Attached Files Preview */}
        <AttachedFilesPreview
          files={attachedFiles}
          onRemoveFile={handleRemoveAttachedFile}
          onClearAll={handleClearAttachedFiles}
        />
        
        {/* Input Form — anexo e enviar circulares dentro do mesmo container,
            estilo do TextAudioInput do onboarding. */}
        <form onSubmit={handleSend} style={styles.inputForm}>
          <div style={styles.inputWrapper}>
            {audioRecorder.isRecording ? (
              <RecordingBar
                seconds={audioRecorder.seconds}
                sending={sendingAudio}
                onCancel={handleCancelRecording}
                onSend={handleStopAndSendAudio}
                colors={colors}
              />
            ) : (
              <>
                {/* Editando: só texto, sem anexo. */}
                {!(messageActionsOn && editing) && (
                  <AttachButton
                    channel="wa_personal"
                    buttonStyle={styles.inputActionBtn}
                    onUploaded={handleFilesUploaded}
                    onError={handleUploadError}
                    onUploadingChange={setUploading}
                  />
                )}
                <textarea
                  ref={attachInputRef}
                  placeholder={messageActionsOn && editing ? 'Edite a mensagem' : 'Digite uma mensagem'}
                  value={newMessage}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  onKeyDown={handleComposerKeyDown}
                  rows={1}
                  disabled={sending}
                  style={styles.textarea}
                />
                {canSend ? (
                  <button
                    type="submit"
                    style={{
                      ...styles.sendBtn,
                      ...(sending ? styles.sendBtnDisabled : {}),
                    }}
                    disabled={sending}
                    title={messageActionsOn && editing ? 'Salvar edição' : 'Enviar mensagem'}
                  >
                    {sending ? (
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (messageActionsOn && editing ? (
                      <Check size={16} />
                    ) : (
                      <Send size={16} />
                    ))}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleStartRecording}
                    style={{
                      ...styles.sendBtn,
                      ...(sending || sendingAudio || uploading ? styles.sendBtnDisabled : {}),
                    }}
                    disabled={sending || sendingAudio || uploading}
                    title="Gravar audio"
                  >
                    {sendingAudio ? (
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Mic size={18} />
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </form>
      </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ChatWindow;